import { toRef, ref, computed, provide, defineComponent, openBlock, createElementBlock, normalizeStyle, isRef, toRefs as toRefs$1, customRef, getCurrentScope, onScopeDispose, unref, watch, resolveComponent, createBlock, Transition, withCtx, withDirectives, createElementVNode, normalizeClass, Fragment, renderList, withModifiers, toDisplayString, createCommentVNode, vShow, inject, onMounted, onUpdated, resolveDynamicComponent, createVNode, withKeys, vModelText, nextTick, onBeforeUnmount, markRaw, reactive, createStaticVNode, createTextVNode, renderSlot, TransitionGroup, shallowReadonly } from "vue";
import { GRAPH_NODE_TYPE_PREFIX, NodeInterface as NodeInterface$1, defineNode, getGraphNodeTypeString, GraphTemplate, Graph, Editor as Editor$1 } from "@baklavajs/core";
import { SequentialHook } from "@baklavajs/events";
let viewModelRef = null;
function providePlugin(viewModel) {
  viewModelRef = viewModel;
}
function useViewModel() {
  if (!viewModelRef) {
    throw new Error("providePlugin() must be called before usePlugin()");
  }
  return {
    viewModel: viewModelRef
  };
}
function useGraph() {
  const { viewModel } = useViewModel();
  return {
    graph: toRef(viewModel.value, "displayedGraph"),
    switchGraph: viewModel.value.switchGraph
  };
}
function useDragMove(positionRef) {
  const { graph } = useGraph();
  const draggingStartPoint = ref(null);
  const draggingStartPosition = ref(null);
  const dragging = computed(() => !!draggingStartPoint.value);
  const onPointerDown = (ev) => {
    draggingStartPoint.value = {
      x: ev.pageX,
      y: ev.pageY
    };
    draggingStartPosition.value = {
      x: positionRef.value.x,
      y: positionRef.value.y
    };
  };
  const onPointerMove = (ev) => {
    if (draggingStartPoint.value) {
      const dx = ev.pageX - draggingStartPoint.value.x;
      const dy = ev.pageY - draggingStartPoint.value.y;
      positionRef.value.x = draggingStartPosition.value.x + dx / graph.value.scaling;
      positionRef.value.y = draggingStartPosition.value.y + dy / graph.value.scaling;
    }
  };
  const onPointerUp = () => {
    draggingStartPoint.value = null;
    draggingStartPosition.value = null;
  };
  return { dragging, onPointerDown, onPointerMove, onPointerUp };
}
function useTransform() {
  const { graph } = useGraph();
  const transform = (x, y) => {
    const tx = x / graph.value.scaling - graph.value.panning.x;
    const ty = y / graph.value.scaling - graph.value.panning.y;
    return [tx, ty];
  };
  return { transform };
}
function usePanZoom() {
  const { graph } = useGraph();
  let pointerCache = [];
  let prevDiff = -1;
  let midpoint = { x: 0, y: 0 };
  const panningRef = computed(() => graph.value.panning);
  const dragMove = useDragMove(panningRef);
  const styles = computed(() => ({
    "transform-origin": "0 0",
    "transform": `scale(${graph.value.scaling}) translate(${graph.value.panning.x}px, ${graph.value.panning.y}px)`
  }));
  const applyZoom = (centerX, centerY, newScale) => {
    const currentPoint = [
      centerX / graph.value.scaling - graph.value.panning.x,
      centerY / graph.value.scaling - graph.value.panning.y
    ];
    const newPoint = [centerX / newScale - graph.value.panning.x, centerY / newScale - graph.value.panning.y];
    const diff = [newPoint[0] - currentPoint[0], newPoint[1] - currentPoint[1]];
    graph.value.panning.x += diff[0];
    graph.value.panning.y += diff[1];
    graph.value.scaling = newScale;
  };
  const onMouseWheel = (ev) => {
    ev.preventDefault();
    let scrollAmount = ev.deltaY;
    if (ev.deltaMode === 1) {
      scrollAmount *= 32;
    }
    const newScale = graph.value.scaling * (1 - scrollAmount / 3e3);
    applyZoom(ev.offsetX, ev.offsetY, newScale);
  };
  const getCoordsFromCache = () => ({
    ax: pointerCache[0].clientX,
    ay: pointerCache[0].clientY,
    bx: pointerCache[1].clientX,
    by: pointerCache[1].clientY
  });
  const onPointerDown = (ev) => {
    pointerCache.push(ev);
    dragMove.onPointerDown(ev);
    if (pointerCache.length === 2) {
      const { ax, ay, bx, by } = getCoordsFromCache();
      midpoint = {
        x: ax + (bx - ax) / 2,
        y: ay + (by - ay) / 2
      };
    }
  };
  const onPointerMove = (ev) => {
    for (let i = 0; i < pointerCache.length; i++) {
      if (ev.pointerId == pointerCache[i].pointerId) {
        pointerCache[i] = ev;
        break;
      }
    }
    if (pointerCache.length == 2) {
      const { ax, ay, bx, by } = getCoordsFromCache();
      const dx = ax - bx;
      const dy = ay - by;
      const curDiff = Math.sqrt(dx * dx + dy * dy);
      if (prevDiff > 0) {
        const newScale = graph.value.scaling * (1 + (curDiff - prevDiff) / 500);
        applyZoom(midpoint.x, midpoint.y, newScale);
      }
      prevDiff = curDiff;
    } else {
      dragMove.onPointerMove(ev);
    }
  };
  const onPointerUp = (ev) => {
    pointerCache = pointerCache.filter((p) => p.pointerId !== ev.pointerId);
    prevDiff = -1;
    dragMove.onPointerUp();
  };
  return { styles, ...dragMove, onPointerDown, onPointerMove, onPointerUp, onMouseWheel };
}
var TemporaryConnectionState = /* @__PURE__ */ ((TemporaryConnectionState2) => {
  TemporaryConnectionState2[TemporaryConnectionState2["NONE"] = 0] = "NONE";
  TemporaryConnectionState2[TemporaryConnectionState2["ALLOWED"] = 1] = "ALLOWED";
  TemporaryConnectionState2[TemporaryConnectionState2["FORBIDDEN"] = 2] = "FORBIDDEN";
  return TemporaryConnectionState2;
})(TemporaryConnectionState || {});
function useTemporaryConnection() {
  const { graph } = useGraph();
  const temporaryConnection = ref(null);
  const hoveringOver = ref(null);
  const onMouseMove = (ev) => {
    if (temporaryConnection.value) {
      temporaryConnection.value.mx = ev.offsetX / graph.value.scaling - graph.value.panning.x;
      temporaryConnection.value.my = ev.offsetY / graph.value.scaling - graph.value.panning.y;
    }
  };
  const onMouseDown = () => {
    if (hoveringOver.value) {
      const connection = graph.value.connections.find((c) => c.to === hoveringOver.value);
      if (hoveringOver.value.isInput && connection) {
        temporaryConnection.value = {
          status: TemporaryConnectionState.NONE,
          from: connection.from
        };
        graph.value.removeConnection(connection);
      } else {
        temporaryConnection.value = {
          status: TemporaryConnectionState.NONE,
          from: hoveringOver.value
        };
      }
      temporaryConnection.value.mx = void 0;
      temporaryConnection.value.my = void 0;
    }
  };
  const onMouseUp = () => {
    if (temporaryConnection.value && hoveringOver.value) {
      graph.value.addConnection(temporaryConnection.value.from, temporaryConnection.value.to);
    }
    temporaryConnection.value = null;
  };
  const hoveredOver = (ni) => {
    hoveringOver.value = ni ?? null;
    if (ni && temporaryConnection.value) {
      temporaryConnection.value.to = ni;
      const checkConnectionResult = graph.value.checkConnection(
        temporaryConnection.value.from,
        temporaryConnection.value.to
      );
      temporaryConnection.value.status = checkConnectionResult.connectionAllowed ? TemporaryConnectionState.ALLOWED : TemporaryConnectionState.FORBIDDEN;
      if (checkConnectionResult.connectionAllowed) {
        const ids = checkConnectionResult.connectionsInDanger.map((c) => c.id);
        graph.value.connections.forEach((c) => {
          if (ids.includes(c.id)) {
            c.isInDanger = true;
          }
        });
      }
    } else if (!ni && temporaryConnection.value) {
      temporaryConnection.value.to = void 0;
      temporaryConnection.value.status = TemporaryConnectionState.NONE;
      graph.value.connections.forEach((c) => {
        c.isInDanger = false;
      });
    }
  };
  provide("hoveredOver", hoveredOver);
  return { temporaryConnection, onMouseMove, onMouseDown, onMouseUp };
}
const _sfc_main$u = defineComponent({
  setup() {
    const { viewModel } = useViewModel();
    const { graph } = useGraph();
    const styles = computed(() => {
      const config = viewModel.value.settings.background;
      const positionLeft = graph.value.panning.x * graph.value.scaling;
      const positionTop = graph.value.panning.y * graph.value.scaling;
      const size = graph.value.scaling * config.gridSize;
      const subSize = size / config.gridDivision;
      const backgroundSize = `${size}px ${size}px, ${size}px ${size}px`;
      const subGridBackgroundSize = graph.value.scaling > config.subGridVisibleThreshold ? `, ${subSize}px ${subSize}px, ${subSize}px ${subSize}px` : "";
      return {
        backgroundPosition: `left ${positionLeft}px top ${positionTop}px`,
        backgroundSize: `${backgroundSize} ${subGridBackgroundSize}`
      };
    });
    return { styles };
  }
});
const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
function _sfc_render$t(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: "background",
    style: normalizeStyle(_ctx.styles)
  }, null, 4);
}
const Background = /* @__PURE__ */ _export_sfc(_sfc_main$u, [["render", _sfc_render$t]]);
var _a;
const isClient = typeof window !== "undefined";
const isString = (val) => typeof val === "string";
const noop = () => {
};
isClient && ((_a = window == null ? void 0 : window.navigator) == null ? void 0 : _a.userAgent) && /iP(ad|hone|od)/.test(window.navigator.userAgent);
function resolveUnref(r) {
  return typeof r === "function" ? r() : unref(r);
}
function identity(arg) {
  return arg;
}
function objectPick(obj, keys2, omitUndefined = false) {
  return keys2.reduce((n, k) => {
    if (k in obj) {
      if (!omitUndefined || obj[k] !== void 0)
        n[k] = obj[k];
    }
    return n;
  }, {});
}
function tryOnScopeDispose(fn) {
  if (getCurrentScope()) {
    onScopeDispose(fn);
    return true;
  }
  return false;
}
var __defProp$7 = Object.defineProperty;
var __defProps$5 = Object.defineProperties;
var __getOwnPropDescs$5 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$9 = Object.getOwnPropertySymbols;
var __hasOwnProp$9 = Object.prototype.hasOwnProperty;
var __propIsEnum$9 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$7 = (obj, key, value) => key in obj ? __defProp$7(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$7 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$9.call(b, prop))
      __defNormalProp$7(a, prop, b[prop]);
  if (__getOwnPropSymbols$9)
    for (var prop of __getOwnPropSymbols$9(b)) {
      if (__propIsEnum$9.call(b, prop))
        __defNormalProp$7(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$5 = (a, b) => __defProps$5(a, __getOwnPropDescs$5(b));
function toRefs(objectRef) {
  if (!isRef(objectRef))
    return toRefs$1(objectRef);
  const result = Array.isArray(objectRef.value) ? new Array(objectRef.value.length) : {};
  for (const key in objectRef.value) {
    result[key] = customRef(() => ({
      get() {
        return objectRef.value[key];
      },
      set(v) {
        if (Array.isArray(objectRef.value)) {
          const copy = [...objectRef.value];
          copy[key] = v;
          objectRef.value = copy;
        } else {
          const newObject = __spreadProps$5(__spreadValues$7({}, objectRef.value), { [key]: v });
          Object.setPrototypeOf(newObject, objectRef.value);
          objectRef.value = newObject;
        }
      }
    }));
  }
  return result;
}
function unrefElement(elRef) {
  var _a2;
  const plain = resolveUnref(elRef);
  return (_a2 = plain == null ? void 0 : plain.$el) != null ? _a2 : plain;
}
const defaultWindow = isClient ? window : void 0;
isClient ? window.document : void 0;
isClient ? window.navigator : void 0;
isClient ? window.location : void 0;
function useEventListener(...args) {
  let target;
  let events;
  let listeners;
  let options;
  if (isString(args[0]) || Array.isArray(args[0])) {
    [events, listeners, options] = args;
    target = defaultWindow;
  } else {
    [target, events, listeners, options] = args;
  }
  if (!target)
    return noop;
  if (!Array.isArray(events))
    events = [events];
  if (!Array.isArray(listeners))
    listeners = [listeners];
  const cleanups = [];
  const cleanup = () => {
    cleanups.forEach((fn) => fn());
    cleanups.length = 0;
  };
  const register = (el, event, listener) => {
    el.addEventListener(event, listener, options);
    return () => el.removeEventListener(event, listener, options);
  };
  const stopWatch = watch(() => unrefElement(target), (el) => {
    cleanup();
    if (!el)
      return;
    cleanups.push(...events.flatMap((event) => {
      return listeners.map((listener) => register(el, event, listener));
    }));
  }, { immediate: true, flush: "post" });
  const stop = () => {
    stopWatch();
    cleanup();
  };
  tryOnScopeDispose(stop);
  return stop;
}
function onClickOutside(target, handler, options = {}) {
  const { window: window2 = defaultWindow, ignore = [], capture = true, detectIframe = false } = options;
  if (!window2)
    return;
  let shouldListen = true;
  let fallback;
  const shouldIgnore = (event) => {
    return ignore.some((target2) => {
      if (typeof target2 === "string") {
        return Array.from(window2.document.querySelectorAll(target2)).some((el) => el === event.target || event.composedPath().includes(el));
      } else {
        const el = unrefElement(target2);
        return el && (event.target === el || event.composedPath().includes(el));
      }
    });
  };
  const listener = (event) => {
    window2.clearTimeout(fallback);
    const el = unrefElement(target);
    if (!el || el === event.target || event.composedPath().includes(el))
      return;
    if (event.detail === 0)
      shouldListen = !shouldIgnore(event);
    if (!shouldListen) {
      shouldListen = true;
      return;
    }
    handler(event);
  };
  const cleanup = [
    useEventListener(window2, "click", listener, { passive: true, capture }),
    useEventListener(window2, "pointerdown", (e) => {
      const el = unrefElement(target);
      if (el)
        shouldListen = !e.composedPath().includes(el) && !shouldIgnore(e);
    }, { passive: true }),
    useEventListener(window2, "pointerup", (e) => {
      if (e.button === 0) {
        const path = e.composedPath();
        e.composedPath = () => path;
        fallback = window2.setTimeout(() => listener(e), 50);
      }
    }, { passive: true }),
    detectIframe && useEventListener(window2, "blur", (event) => {
      var _a2;
      const el = unrefElement(target);
      if (((_a2 = window2.document.activeElement) == null ? void 0 : _a2.tagName) === "IFRAME" && !(el == null ? void 0 : el.contains(window2.document.activeElement)))
        handler(event);
    })
  ].filter(Boolean);
  const stop = () => cleanup.forEach((fn) => fn());
  return stop;
}
const _global = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : {};
const globalKey = "__vueuse_ssr_handlers__";
_global[globalKey] = _global[globalKey] || {};
_global[globalKey];
var __defProp$5 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$5 = Object.getOwnPropertySymbols;
var __hasOwnProp$5 = Object.prototype.hasOwnProperty;
var __propIsEnum$5 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$5 = (obj, key, value) => key in obj ? __defProp$5(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$5 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$5.call(b, prop))
      __defNormalProp$5(a, prop, b[prop]);
  if (__getOwnPropSymbols$5)
    for (var prop of __getOwnPropSymbols$5(b)) {
      if (__propIsEnum$5.call(b, prop))
        __defNormalProp$5(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
const defaultState = {
  x: 0,
  y: 0,
  pointerId: 0,
  pressure: 0,
  tiltX: 0,
  tiltY: 0,
  width: 0,
  height: 0,
  twist: 0,
  pointerType: null
};
const keys = /* @__PURE__ */ Object.keys(defaultState);
function usePointer(options = {}) {
  const {
    target = defaultWindow
  } = options;
  const isInside = ref(false);
  const state = ref(options.initialValue || {});
  Object.assign(state.value, defaultState, state.value);
  const handler = (event) => {
    isInside.value = true;
    if (options.pointerTypes && !options.pointerTypes.includes(event.pointerType))
      return;
    state.value = objectPick(event, keys, false);
  };
  if (target) {
    useEventListener(target, "pointerdown", handler, { passive: true });
    useEventListener(target, "pointermove", handler, { passive: true });
    useEventListener(target, "pointerleave", () => isInside.value = false, { passive: true });
  }
  return __spreadProps$1(__spreadValues$5({}, toRefs(state)), {
    isInside
  });
}
var SwipeDirection;
(function(SwipeDirection2) {
  SwipeDirection2["UP"] = "UP";
  SwipeDirection2["RIGHT"] = "RIGHT";
  SwipeDirection2["DOWN"] = "DOWN";
  SwipeDirection2["LEFT"] = "LEFT";
  SwipeDirection2["NONE"] = "NONE";
})(SwipeDirection || (SwipeDirection = {}));
var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const _TransitionPresets = {
  easeInSine: [0.12, 0, 0.39, 0],
  easeOutSine: [0.61, 1, 0.88, 1],
  easeInOutSine: [0.37, 0, 0.63, 1],
  easeInQuad: [0.11, 0, 0.5, 0],
  easeOutQuad: [0.5, 1, 0.89, 1],
  easeInOutQuad: [0.45, 0, 0.55, 1],
  easeInCubic: [0.32, 0, 0.67, 0],
  easeOutCubic: [0.33, 1, 0.68, 1],
  easeInOutCubic: [0.65, 0, 0.35, 1],
  easeInQuart: [0.5, 0, 0.75, 0],
  easeOutQuart: [0.25, 1, 0.5, 1],
  easeInOutQuart: [0.76, 0, 0.24, 1],
  easeInQuint: [0.64, 0, 0.78, 0],
  easeOutQuint: [0.22, 1, 0.36, 1],
  easeInOutQuint: [0.83, 0, 0.17, 1],
  easeInExpo: [0.7, 0, 0.84, 0],
  easeOutExpo: [0.16, 1, 0.3, 1],
  easeInOutExpo: [0.87, 0, 0.13, 1],
  easeInCirc: [0.55, 0, 1, 0.45],
  easeOutCirc: [0, 0.55, 0.45, 1],
  easeInOutCirc: [0.85, 0, 0.15, 1],
  easeInBack: [0.36, 0, 0.66, -0.56],
  easeOutBack: [0.34, 1.56, 0.64, 1],
  easeInOutBack: [0.68, -0.6, 0.32, 1.6]
};
__spreadValues({
  linear: identity
}, _TransitionPresets);
const _sfc_main$t = defineComponent({
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      required: true
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    isNested: {
      type: Boolean,
      default: false
    },
    isFlipped: {
      type: Object,
      default: () => ({ x: false, y: false })
    },
    flippable: {
      type: Boolean,
      default: false
    }
  },
  emits: ["click", "update:modelValue"],
  setup(props, { emit }) {
    let activeMenuResetTimeout = null;
    const el = ref(null);
    const activeMenu = ref(-1);
    const height = ref(0);
    const rootIsFlipped = ref({ x: false, y: false });
    const flippedX = computed(() => props.flippable && (rootIsFlipped.value.x || props.isFlipped.x));
    const flippedY = computed(() => props.flippable && (rootIsFlipped.value.y || props.isFlipped.y));
    const styles = computed(() => {
      const s = {};
      if (!props.isNested) {
        s.top = (flippedY.value ? props.y - height.value : props.y) + "px";
        s.left = props.x + "px";
      }
      return s;
    });
    const classes = computed(() => {
      return {
        "--flipped-x": flippedX.value,
        "--flipped-y": flippedY.value,
        "--nested": props.isNested
      };
    });
    const itemsWithHoverProperty = computed(() => props.items.map((i) => ({ ...i, hover: false })));
    watch([() => props.y, () => props.items], () => {
      var _a2, _b, _c, _d;
      height.value = props.items.length * 30;
      const parentWidth = ((_b = (_a2 = el.value) == null ? void 0 : _a2.parentElement) == null ? void 0 : _b.offsetWidth) ?? 0;
      const parentHeight = ((_d = (_c = el.value) == null ? void 0 : _c.parentElement) == null ? void 0 : _d.offsetHeight) ?? 0;
      rootIsFlipped.value.x = !props.isNested && props.x > parentWidth * 0.75;
      rootIsFlipped.value.y = !props.isNested && props.y + height.value > parentHeight - 20;
    });
    onClickOutside(el, () => {
      if (props.modelValue) {
        emit("update:modelValue", false);
      }
    });
    const onClick = (item) => {
      if (!item.submenu && item.value) {
        emit("click", item.value);
        emit("update:modelValue", false);
      }
    };
    const onChildClick = (value) => {
      emit("click", value);
      activeMenu.value = -1;
      if (!props.isNested) {
        emit("update:modelValue", false);
      }
    };
    const onMouseEnter = (event, index) => {
      if (props.items[index].submenu) {
        activeMenu.value = index;
        if (activeMenuResetTimeout !== null) {
          clearTimeout(activeMenuResetTimeout);
          activeMenuResetTimeout = null;
        }
      }
    };
    const onMouseLeave = (event, index) => {
      if (props.items[index].submenu) {
        activeMenuResetTimeout = window.setTimeout(() => {
          activeMenu.value = -1;
          activeMenuResetTimeout = null;
        }, 200);
      }
    };
    return {
      el,
      activeMenu,
      flippedX,
      flippedY,
      styles,
      classes,
      itemsWithHoverProperty,
      onClick,
      onChildClick,
      onClickOutside,
      onMouseEnter,
      onMouseLeave
    };
  }
});
const _hoisted_1$p = ["onMouseenter", "onMouseleave", "onClick"];
const _hoisted_2$j = { class: "flex-fill" };
const _hoisted_3$f = {
  key: 0,
  class: "__submenu-icon",
  style: { "line-height": "1em" }
};
const _hoisted_4$d = /* @__PURE__ */ createElementVNode("svg", {
  width: "13",
  height: "13",
  viewBox: "-60 120 250 250"
}, [
  /* @__PURE__ */ createElementVNode("path", {
    d: "M160.875 279.5625 L70.875 369.5625 L70.875 189.5625 L160.875 279.5625 Z",
    stroke: "none",
    fill: "white"
  })
], -1);
const _hoisted_5$7 = [
  _hoisted_4$d
];
function _sfc_render$s(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_context_menu = resolveComponent("context-menu", true);
  return openBlock(), createBlock(Transition, { name: "slide-fade" }, {
    default: withCtx(() => [
      withDirectives(createElementVNode("div", {
        ref: "el",
        class: normalizeClass(["baklava-context-menu", _ctx.classes]),
        style: normalizeStyle(_ctx.styles)
      }, [
        (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.itemsWithHoverProperty, (item, index) => {
          return openBlock(), createElementBlock(Fragment, null, [
            item.isDivider ? (openBlock(), createElementBlock("div", {
              key: `d-${index}`,
              class: "divider"
            })) : (openBlock(), createElementBlock("div", {
              key: `i-${index}`,
              class: normalizeClass(["item", { "submenu": !!item.submenu, "--disabled": !!item.disabled }]),
              onMouseenter: ($event) => _ctx.onMouseEnter($event, index),
              onMouseleave: ($event) => _ctx.onMouseLeave($event, index),
              onClick: withModifiers(($event) => _ctx.onClick(item), ["stop", "prevent"])
            }, [
              createElementVNode("div", _hoisted_2$j, toDisplayString(item.label), 1),
              item.submenu ? (openBlock(), createElementBlock("div", _hoisted_3$f, _hoisted_5$7)) : createCommentVNode("", true),
              item.submenu ? (openBlock(), createBlock(_component_context_menu, {
                key: 1,
                value: _ctx.activeMenu === index,
                items: item.submenu,
                "is-nested": true,
                "is-flipped": { x: _ctx.flippedX, y: _ctx.flippedY },
                flippable: _ctx.flippable,
                onClick: _ctx.onChildClick
              }, null, 8, ["value", "items", "is-flipped", "flippable", "onClick"])) : createCommentVNode("", true)
            ], 42, _hoisted_1$p))
          ], 64);
        }), 256))
      ], 6), [
        [vShow, _ctx.modelValue]
      ])
    ]),
    _: 1
  });
}
const ContextMenu = /* @__PURE__ */ _export_sfc(_sfc_main$t, [["render", _sfc_render$s]]);
const _sfc_main$s = {};
const _hoisted_1$o = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "16",
  height: "16",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2$i = /* @__PURE__ */ createElementVNode("path", {
  stroke: "none",
  d: "M0 0h24v24H0z",
  fill: "none"
}, null, -1);
const _hoisted_3$e = /* @__PURE__ */ createElementVNode("circle", {
  cx: "12",
  cy: "12",
  r: "1"
}, null, -1);
const _hoisted_4$c = /* @__PURE__ */ createElementVNode("circle", {
  cx: "12",
  cy: "19",
  r: "1"
}, null, -1);
const _hoisted_5$6 = /* @__PURE__ */ createElementVNode("circle", {
  cx: "12",
  cy: "5",
  r: "1"
}, null, -1);
const _hoisted_6$4 = [
  _hoisted_2$i,
  _hoisted_3$e,
  _hoisted_4$c,
  _hoisted_5$6
];
function _sfc_render$r(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$o, _hoisted_6$4);
}
const VerticalDots = /* @__PURE__ */ _export_sfc(_sfc_main$s, [["render", _sfc_render$r]]);
const _sfc_main$r = defineComponent({
  props: {
    node: {
      type: Object,
      required: true
    },
    intf: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { viewModel } = useViewModel();
    const hoveredOver = inject("hoveredOver");
    const el = ref(null);
    const isConnected = computed(() => props.intf.connectionCount > 0);
    const classes = computed(() => ({
      "--input": props.intf.isInput,
      "--output": !props.intf.isInput,
      "--connected": isConnected.value
    }));
    const showComponent = computed(
      () => props.intf.component && props.intf.connectionCount === 0 && (props.intf.isInput || !props.intf.port)
    );
    const startHover = () => {
      hoveredOver(props.intf);
    };
    const endHover = () => {
      hoveredOver(void 0);
    };
    const onRender = () => {
      if (el.value) {
        viewModel.value.hooks.renderInterface.execute({ intf: props.intf, el: el.value });
      }
    };
    const openSidebar = () => {
      const sidebar = viewModel.value.displayedGraph.sidebar;
      sidebar.nodeId = props.node.id;
      sidebar.optionName = props.intf.name;
      sidebar.visible = true;
    };
    onMounted(onRender);
    onUpdated(onRender);
    return { el, isConnected, classes, showComponent, startHover, endHover, openSidebar };
  }
});
const _hoisted_1$n = ["id"];
const _hoisted_2$h = {
  key: 2,
  class: "align-middle"
};
function _sfc_render$q(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    id: _ctx.intf.id,
    ref: "el",
    class: normalizeClass(["baklava-node-interface", _ctx.classes])
  }, [
    _ctx.intf.port ? (openBlock(), createElementBlock("div", {
      key: 0,
      class: "__port",
      onPointerover: _cache[0] || (_cache[0] = (...args) => _ctx.startHover && _ctx.startHover(...args)),
      onPointerout: _cache[1] || (_cache[1] = (...args) => _ctx.endHover && _ctx.endHover(...args))
    }, null, 32)) : createCommentVNode("", true),
    _ctx.showComponent ? (openBlock(), createBlock(resolveDynamicComponent(_ctx.intf.component), {
      key: 1,
      modelValue: _ctx.intf.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => _ctx.intf.value = $event),
      node: _ctx.node,
      intf: _ctx.intf,
      onOpenSidebar: _ctx.openSidebar
    }, null, 40, ["modelValue", "node", "intf", "onOpenSidebar"])) : (openBlock(), createElementBlock("span", _hoisted_2$h, toDisplayString(_ctx.intf.name), 1))
  ], 10, _hoisted_1$n);
}
const NodeInterface = /* @__PURE__ */ _export_sfc(_sfc_main$r, [["render", _sfc_render$q]]);
const _hoisted_1$m = ["id", "data-node-type"];
const _hoisted_2$g = ["onPointerdown"];
const _hoisted_3$d = { class: "__title-label" };
const _hoisted_4$b = { class: "__menu" };
const _hoisted_5$5 = ["onKeydown"];
const _hoisted_6$3 = { class: "__content" };
const _hoisted_7$1 = { class: "__outputs" };
const _hoisted_8$1 = { class: "__inputs" };
const _sfc_main$q = /* @__PURE__ */ defineComponent({
  __name: "Node",
  props: {
    node: null,
    selected: { type: Boolean, default: false }
  },
  emits: ["select"],
  setup(__props, { emit }) {
    const props = __props;
    const { viewModel } = useViewModel();
    const { graph, switchGraph } = useGraph();
    const dragMove = useDragMove(toRef(props.node, "position"));
    const el = ref(null);
    const renaming = ref(false);
    const tempName = ref("");
    const renameInputEl = ref(null);
    const showContextMenu = ref(false);
    const contextMenuItems = computed(() => {
      const items = [
        { value: "rename", label: "Rename" },
        { value: "delete", label: "Delete" }
      ];
      if (props.node.type.startsWith(GRAPH_NODE_TYPE_PREFIX)) {
        items.push({ value: "editSubgraph", label: "Edit Subgraph" });
      }
      return items;
    });
    const classes = computed(() => ({
      "--selected": props.selected,
      "--dragging": dragMove.dragging.value,
      "--two-column": !!props.node.twoColumn
    }));
    const styles = computed(() => {
      var _a2, _b;
      return {
        top: `${((_a2 = props.node.position) == null ? void 0 : _a2.y) ?? 0}px`,
        left: `${((_b = props.node.position) == null ? void 0 : _b.x) ?? 0}px`,
        width: `${props.node.width ?? 200}px`
      };
    });
    const displayedInputs = computed(() => Object.values(props.node.inputs).filter((ni) => !ni.hidden));
    const displayedOutputs = computed(() => Object.values(props.node.outputs).filter((ni) => !ni.hidden));
    const select = () => {
      emit("select");
    };
    const startDrag = (ev) => {
      dragMove.onPointerDown(ev);
      document.addEventListener("pointermove", dragMove.onPointerMove);
      document.addEventListener("pointerup", stopDrag);
      select();
    };
    const stopDrag = () => {
      dragMove.onPointerUp();
      document.removeEventListener("pointermove", dragMove.onPointerMove);
      document.removeEventListener("pointerup", stopDrag);
    };
    const openContextMenu = () => {
      showContextMenu.value = true;
    };
    const onContextMenuClick = async (action) => {
      var _a2;
      switch (action) {
        case "delete":
          graph.value.removeNode(props.node);
          break;
        case "rename":
          tempName.value = props.node.title;
          renaming.value = true;
          await nextTick();
          (_a2 = renameInputEl.value) == null ? void 0 : _a2.focus();
          break;
        case "editSubgraph":
          switchGraph(props.node.template);
          break;
      }
    };
    const doneRenaming = () => {
      props.node.title = tempName.value;
      renaming.value = false;
    };
    const onRender = () => {
      if (el.value) {
        viewModel.value.hooks.renderNode.execute({ node: props.node, el: el.value });
      }
    };
    onMounted(onRender);
    onUpdated(onRender);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        id: __props.node.id,
        ref_key: "el",
        ref: el,
        class: normalizeClass(["baklava-node", unref(classes)]),
        style: normalizeStyle(unref(styles)),
        "data-node-type": __props.node.type,
        onPointerdown: select
      }, [
        createElementVNode("div", {
          class: "__title",
          onPointerdown: withModifiers(startDrag, ["self", "stop"])
        }, [
          !renaming.value ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
            createElementVNode("div", _hoisted_3$d, toDisplayString(__props.node.title), 1),
            createElementVNode("div", _hoisted_4$b, [
              createVNode(VerticalDots, {
                class: "--clickable",
                onClick: openContextMenu
              }),
              createVNode(ContextMenu, {
                modelValue: showContextMenu.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => showContextMenu.value = $event),
                x: 0,
                y: 0,
                items: unref(contextMenuItems),
                onClick: onContextMenuClick
              }, null, 8, ["modelValue", "items"])
            ])
          ], 64)) : withDirectives((openBlock(), createElementBlock("input", {
            key: 1,
            ref_key: "renameInputEl",
            ref: renameInputEl,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => tempName.value = $event),
            type: "text",
            class: "baklava-input",
            placeholder: "Node Name",
            onBlur: doneRenaming,
            onKeydown: withKeys(doneRenaming, ["enter"])
          }, null, 40, _hoisted_5$5)), [
            [vModelText, tempName.value]
          ])
        ], 40, _hoisted_2$g),
        createElementVNode("div", _hoisted_6$3, [
          createElementVNode("div", _hoisted_7$1, [
            (openBlock(true), createElementBlock(Fragment, null, renderList(unref(displayedOutputs), (output) => {
              return openBlock(), createBlock(NodeInterface, {
                key: output.id,
                node: __props.node,
                intf: output
              }, null, 8, ["node", "intf"]);
            }), 128))
          ]),
          createElementVNode("div", _hoisted_8$1, [
            (openBlock(true), createElementBlock(Fragment, null, renderList(unref(displayedInputs), (input) => {
              return openBlock(), createBlock(NodeInterface, {
                key: input.id,
                node: __props.node,
                intf: input
              }, null, 8, ["node", "intf"]);
            }), 128))
          ])
        ])
      ], 46, _hoisted_1$m);
    };
  }
});
const _sfc_main$p = defineComponent({
  props: {
    x1: {
      type: Number,
      required: true
    },
    y1: {
      type: Number,
      required: true
    },
    x2: {
      type: Number,
      required: true
    },
    y2: {
      type: Number,
      required: true
    },
    state: {
      type: Number,
      default: TemporaryConnectionState.NONE
    },
    isTemporary: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { viewModel } = useViewModel();
    const { graph } = useGraph();
    const transform = (x, y) => {
      const tx = (x + graph.value.panning.x) * graph.value.scaling;
      const ty = (y + graph.value.panning.y) * graph.value.scaling;
      return [tx, ty];
    };
    const d = computed(() => {
      const [tx1, ty1] = transform(props.x1, props.y1);
      const [tx2, ty2] = transform(props.x2, props.y2);
      if (viewModel.value.settings.useStraightConnections) {
        return `M ${tx1} ${ty1} L ${tx2} ${ty2}`;
      } else {
        const dx = 0.3 * Math.abs(tx1 - tx2);
        return `M ${tx1} ${ty1} C ${tx1 + dx} ${ty1}, ${tx2 - dx} ${ty2}, ${tx2} ${ty2}`;
      }
    });
    const classes = computed(() => ({
      "--temporary": props.isTemporary,
      "--allowed": props.state === TemporaryConnectionState.ALLOWED,
      "--forbidden": props.state === TemporaryConnectionState.FORBIDDEN
    }));
    return { d, classes };
  }
});
const _hoisted_1$l = ["d"];
function _sfc_render$p(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("path", {
    class: normalizeClass(["baklava-connection", _ctx.classes]),
    d: _ctx.d
  }, null, 10, _hoisted_1$l);
}
const ConnectionView = /* @__PURE__ */ _export_sfc(_sfc_main$p, [["render", _sfc_render$p]]);
function getDomElementOfNode(node) {
  return document.getElementById(node.id);
}
function getDomElements(ni) {
  const interfaceDOM = document.getElementById(ni.id);
  const portDOM = interfaceDOM == null ? void 0 : interfaceDOM.getElementsByClassName("__port");
  return {
    node: (interfaceDOM == null ? void 0 : interfaceDOM.closest(".baklava-node")) ?? null,
    interface: interfaceDOM,
    port: portDOM && portDOM.length > 0 ? portDOM[0] : null
  };
}
const _sfc_main$o = defineComponent({
  components: {
    "connection-view": ConnectionView
  },
  props: {
    connection: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { graph } = useGraph();
    let resizeObserver;
    const d = ref({ x1: 0, y1: 0, x2: 0, y2: 0 });
    const state = computed(
      () => props.connection.isInDanger ? TemporaryConnectionState.FORBIDDEN : TemporaryConnectionState.NONE
    );
    const fromNodePosition = computed(() => {
      var _a2;
      return (_a2 = graph.value.findNodeById(props.connection.from.nodeId)) == null ? void 0 : _a2.position;
    });
    const toNodePosition = computed(() => {
      var _a2;
      return (_a2 = graph.value.findNodeById(props.connection.to.nodeId)) == null ? void 0 : _a2.position;
    });
    const getPortCoordinates2 = (resolved) => {
      if (resolved.node && resolved.interface && resolved.port) {
        return [
          resolved.node.offsetLeft + resolved.interface.offsetLeft + resolved.port.offsetLeft + resolved.port.clientWidth / 2,
          resolved.node.offsetTop + resolved.interface.offsetTop + resolved.port.offsetTop + resolved.port.clientHeight / 2
        ];
      } else {
        return [0, 0];
      }
    };
    const updateCoords = () => {
      const from = getDomElements(props.connection.from);
      const to = getDomElements(props.connection.to);
      if (from.node && to.node) {
        if (!resizeObserver) {
          resizeObserver = new ResizeObserver(() => {
            updateCoords();
          });
          resizeObserver.observe(from.node);
          resizeObserver.observe(to.node);
        }
      }
      const [x1, y1] = getPortCoordinates2(from);
      const [x2, y2] = getPortCoordinates2(to);
      d.value = { x1, y1, x2, y2 };
    };
    onMounted(async () => {
      await nextTick();
      updateCoords();
    });
    onBeforeUnmount(() => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    });
    watch([fromNodePosition, toNodePosition], () => updateCoords(), { deep: true });
    return { d, state };
  }
});
function _sfc_render$o(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_connection_view = resolveComponent("connection-view");
  return openBlock(), createBlock(_component_connection_view, {
    x1: _ctx.d.x1,
    y1: _ctx.d.y1,
    x2: _ctx.d.x2,
    y2: _ctx.d.y2,
    state: _ctx.state
  }, null, 8, ["x1", "y1", "x2", "y2", "state"]);
}
const ConnectionWrapper = /* @__PURE__ */ _export_sfc(_sfc_main$o, [["render", _sfc_render$o]]);
function getPortCoordinates(resolved) {
  if (resolved.node && resolved.interface && resolved.port) {
    return [
      resolved.node.offsetLeft + resolved.interface.offsetLeft + resolved.port.offsetLeft + resolved.port.clientWidth / 2,
      resolved.node.offsetTop + resolved.interface.offsetTop + resolved.port.offsetTop + resolved.port.clientHeight / 2
    ];
  } else {
    return [0, 0];
  }
}
const _sfc_main$n = defineComponent({
  components: {
    "connection-view": ConnectionView
  },
  props: {
    connection: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const status = computed(() => props.connection ? props.connection.status : TemporaryConnectionState.NONE);
    const d = computed(() => {
      if (!props.connection) {
        return {
          input: [0, 0],
          output: [0, 0]
        };
      }
      const start = getPortCoordinates(getDomElements(props.connection.from));
      const end = props.connection.to ? getPortCoordinates(getDomElements(props.connection.to)) : [props.connection.mx || start[0], props.connection.my || start[1]];
      if (props.connection.from.isInput) {
        return {
          input: end,
          output: start
        };
      } else {
        return {
          input: start,
          output: end
        };
      }
    });
    return { d, status };
  }
});
function _sfc_render$n(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_connection_view = resolveComponent("connection-view");
  return openBlock(), createBlock(_component_connection_view, {
    x1: _ctx.d.input[0],
    y1: _ctx.d.input[1],
    x2: _ctx.d.output[0],
    y2: _ctx.d.output[1],
    state: _ctx.status,
    "is-temporary": ""
  }, null, 8, ["x1", "y1", "x2", "y2", "state"]);
}
const TemporaryConnection = /* @__PURE__ */ _export_sfc(_sfc_main$n, [["render", _sfc_render$n]]);
const _sfc_main$m = defineComponent({
  setup() {
    const { graph } = useGraph();
    const el = ref(null);
    const width = ref(300);
    const node = computed(() => {
      const id = graph.value.sidebar.nodeId;
      return graph.value.nodes.find((x) => x.id === id);
    });
    const styles = computed(() => ({
      width: `${width.value}px`
    }));
    const displayedInterfaces = computed(() => {
      if (!node.value) {
        return [];
      }
      const allIntfs = [...Object.values(node.value.inputs), ...Object.values(node.value.outputs)];
      return allIntfs.filter((intf) => intf.displayInSidebar && intf.component);
    });
    const close = () => {
      graph.value.sidebar.visible = false;
    };
    const startResize = () => {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener(
        "mouseup",
        () => {
          window.removeEventListener("mousemove", onMouseMove);
        },
        { once: true }
      );
    };
    const onMouseMove = (event) => {
      var _a2, _b;
      const maxwidth = ((_b = (_a2 = el.value) == null ? void 0 : _a2.parentElement) == null ? void 0 : _b.getBoundingClientRect().width) ?? 500;
      width.value -= event.movementX;
      if (width.value < 300) {
        width.value = 300;
      } else if (width.value > 0.9 * maxwidth) {
        width.value = 0.9 * maxwidth;
      }
    };
    return { el, graph, node, styles, displayedInterfaces, startResize, close };
  }
});
const _hoisted_1$k = { class: "__header" };
const _hoisted_2$f = { class: "__node-name" };
function _sfc_render$m(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    ref: "el",
    class: normalizeClass(["baklava-sidebar", { "--open": _ctx.graph.sidebar.visible }]),
    style: normalizeStyle(_ctx.styles)
  }, [
    createElementVNode("div", {
      class: "__resizer",
      onMousedown: _cache[0] || (_cache[0] = (...args) => _ctx.startResize && _ctx.startResize(...args))
    }, null, 32),
    createElementVNode("div", _hoisted_1$k, [
      createElementVNode("button", {
        tabindex: "-1",
        class: "__close",
        onClick: _cache[1] || (_cache[1] = (...args) => _ctx.close && _ctx.close(...args))
      }, " × "),
      createElementVNode("div", _hoisted_2$f, [
        createElementVNode("b", null, toDisplayString(_ctx.node ? _ctx.node.title : ""), 1)
      ])
    ]),
    (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.displayedInterfaces, (intf) => {
      return openBlock(), createElementBlock("div", {
        key: intf.id,
        class: "__interface"
      }, [
        (openBlock(), createBlock(resolveDynamicComponent(intf.component), {
          modelValue: intf.value,
          "onUpdate:modelValue": ($event) => intf.value = $event,
          node: _ctx.node,
          intf
        }, null, 8, ["modelValue", "onUpdate:modelValue", "node", "intf"]))
      ]);
    }), 128))
  ], 6);
}
const Sidebar = /* @__PURE__ */ _export_sfc(_sfc_main$m, [["render", _sfc_render$m]]);
const _sfc_main$l = defineComponent({
  setup() {
    const { viewModel } = useViewModel();
    const { graph } = useGraph();
    const canvas = ref(null);
    const showViewBounds = ref(false);
    let ctx;
    let dragging = false;
    let bounds = { x1: 0, y1: 0, x2: 0, y2: 0 };
    const updateCanvas = () => {
      var _a2, _b;
      if (!ctx) {
        return;
      }
      const nodeCoords = /* @__PURE__ */ new Map();
      const nodeDomElements = /* @__PURE__ */ new Map();
      for (const n of graph.value.nodes) {
        const domElement = getDomElementOfNode(n);
        const width = (domElement == null ? void 0 : domElement.clientWidth) ?? 0;
        const height = (domElement == null ? void 0 : domElement.clientHeight) ?? 0;
        const posX = ((_a2 = n.position) == null ? void 0 : _a2.x) ?? 0;
        const posY = ((_b = n.position) == null ? void 0 : _b.y) ?? 0;
        nodeCoords.set(n, {
          x1: posX,
          y1: posY,
          x2: posX + width,
          y2: posY + height
        });
        nodeDomElements.set(n, domElement);
      }
      const newBounds = {
        x1: Number.MAX_SAFE_INTEGER,
        y1: Number.MAX_SAFE_INTEGER,
        x2: Number.MIN_SAFE_INTEGER,
        y2: Number.MIN_SAFE_INTEGER
      };
      for (const nc of nodeCoords.values()) {
        if (nc.x1 < newBounds.x1) {
          newBounds.x1 = nc.x1;
        }
        if (nc.y1 < newBounds.y1) {
          newBounds.y1 = nc.y1;
        }
        if (nc.x2 > newBounds.x2) {
          newBounds.x2 = nc.x2;
        }
        if (nc.y2 > newBounds.y2) {
          newBounds.y2 = nc.y2;
        }
      }
      const padding = 50;
      newBounds.x1 -= padding;
      newBounds.y1 -= padding;
      newBounds.x2 += padding;
      newBounds.y2 += padding;
      bounds = newBounds;
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.strokeStyle = "white";
      for (const c of graph.value.connections) {
        const [origX1, origY1] = getPortCoordinates(getDomElements(c.from));
        const [origX2, origY2] = getPortCoordinates(getDomElements(c.to));
        const [x1, y1] = transformCoordinates(origX1, origY1);
        const [x2, y2] = transformCoordinates(origX2, origY2);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        if (viewModel.value.settings.useStraightConnections) {
          ctx.lineTo(x2, y2);
        } else {
          const dx = 0.3 * Math.abs(x1 - x2);
          ctx.bezierCurveTo(x1 + dx, y1, x2 - dx, y2, x2, y2);
        }
        ctx.stroke();
      }
      ctx.strokeStyle = "lightgray";
      for (const [n, nc] of nodeCoords.entries()) {
        const [x1, y1] = transformCoordinates(nc.x1, nc.y1);
        const [x2, y2] = transformCoordinates(nc.x2, nc.y2);
        ctx.fillStyle = getNodeColor(nodeDomElements.get(n));
        ctx.beginPath();
        ctx.rect(x1, y1, x2 - x1, y2 - y1);
        ctx.fill();
        ctx.stroke();
      }
      if (showViewBounds.value) {
        const viewBounds = getViewBounds();
        const [x1, y1] = transformCoordinates(viewBounds.x1, viewBounds.y1);
        const [x2, y2] = transformCoordinates(viewBounds.x2, viewBounds.y2);
        ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
        ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      }
    };
    const transformCoordinates = (origX, origY) => {
      return [
        (origX - bounds.x1) / (bounds.x2 - bounds.x1) * ctx.canvas.clientWidth,
        (origY - bounds.y1) / (bounds.y2 - bounds.y1) * ctx.canvas.clientHeight
      ];
    };
    const reverseTransform = (thisX, thisY) => {
      return [
        thisX * (bounds.x2 - bounds.x1) / ctx.canvas.clientWidth + bounds.x1,
        thisY * (bounds.y2 - bounds.y1) / ctx.canvas.clientHeight + bounds.y1
      ];
    };
    const getNodeColor = (domElement) => {
      if (domElement) {
        const content = domElement.querySelector(".__content");
        if (content) {
          const contentColor = getComputedColor(content);
          if (contentColor) {
            return contentColor;
          }
        }
        const nodeColor = getComputedColor(domElement);
        if (nodeColor) {
          return nodeColor;
        }
      }
      return "gray";
    };
    const getComputedColor = (domElement) => {
      const c = getComputedStyle(domElement).backgroundColor;
      if (c && c !== "rgba(0, 0, 0, 0)") {
        return c;
      }
    };
    const getViewBounds = () => {
      const parentWidth = canvas.value.parentElement.offsetWidth;
      const parentHeight = canvas.value.parentElement.offsetHeight;
      const x2 = parentWidth / graph.value.scaling - graph.value.panning.x;
      const y2 = parentHeight / graph.value.scaling - graph.value.panning.y;
      return { x1: -graph.value.panning.x, y1: -graph.value.panning.y, x2, y2 };
    };
    const mousedown = (ev) => {
      if (ev.button === 0) {
        dragging = true;
        mousemove(ev);
      }
    };
    const mousemove = (ev) => {
      if (dragging) {
        const [cx, cy] = reverseTransform(ev.offsetX, ev.offsetY);
        const viewBounds = getViewBounds();
        const dx = (viewBounds.x1 - viewBounds.x2) / 2;
        const dy = (viewBounds.y1 - viewBounds.y2) / 2;
        graph.value.panning.x = -(cx + dx);
        graph.value.panning.y = -(cy + dy);
      }
    };
    const mouseup = () => {
      dragging = false;
    };
    const mouseenter = () => {
      showViewBounds.value = true;
    };
    const mouseleave = () => {
      showViewBounds.value = false;
      mouseup();
    };
    onMounted(() => {
      ctx = canvas.value.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      updateCanvas();
    });
    return { canvas, showViewBounds, mousedown, mousemove, mouseup, mouseenter, mouseleave };
  }
});
function _sfc_render$l(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("canvas", {
    ref: "canvas",
    class: "baklava-minimap",
    onMouseenter: _cache[0] || (_cache[0] = (...args) => _ctx.mouseenter && _ctx.mouseenter(...args)),
    onMouseleave: _cache[1] || (_cache[1] = (...args) => _ctx.mouseleave && _ctx.mouseleave(...args)),
    onMousedown: _cache[2] || (_cache[2] = withModifiers((...args) => _ctx.mousedown && _ctx.mousedown(...args), ["self"])),
    onMousemove: _cache[3] || (_cache[3] = withModifiers((...args) => _ctx.mousemove && _ctx.mousemove(...args), ["self"])),
    onMouseup: _cache[4] || (_cache[4] = (...args) => _ctx.mouseup && _ctx.mouseup(...args))
  }, null, 544);
}
const Minimap = /* @__PURE__ */ _export_sfc(_sfc_main$l, [["render", _sfc_render$l]]);
const _sfc_main$k = defineComponent({
  components: { ContextMenu, VerticalDots },
  props: {
    type: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const { viewModel } = useViewModel();
    const { switchGraph } = useGraph();
    const showContextMenu = ref(false);
    const hasContextMenu = computed(() => props.type.startsWith(GRAPH_NODE_TYPE_PREFIX));
    const contextMenuItems = [
      { label: "Edit Subgraph", value: "editSubgraph" },
      { label: "Delete Subgraph", value: "deleteSubgraph" }
    ];
    const openContextMenu = () => {
      showContextMenu.value = true;
    };
    const onContextMenuClick = (action) => {
      const graphTemplateId = props.type.substring(GRAPH_NODE_TYPE_PREFIX.length);
      const graphTemplate = viewModel.value.editor.graphTemplates.find((gt) => gt.id === graphTemplateId);
      if (!graphTemplate) {
        return;
      }
      switch (action) {
        case "editSubgraph":
          switchGraph(graphTemplate);
          break;
        case "deleteSubgraph":
          viewModel.value.editor.removeGraphTemplate(graphTemplate);
          break;
      }
    };
    return { showContextMenu, hasContextMenu, contextMenuItems, openContextMenu, onContextMenuClick };
  }
});
const _hoisted_1$j = ["data-node-type"];
const _hoisted_2$e = { class: "__title" };
const _hoisted_3$c = { class: "__title-label" };
const _hoisted_4$a = {
  key: 0,
  class: "__menu"
};
function _sfc_render$k(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_vertical_dots = resolveComponent("vertical-dots");
  const _component_context_menu = resolveComponent("context-menu");
  return openBlock(), createElementBlock("div", {
    class: "baklava-node --palette",
    "data-node-type": _ctx.type
  }, [
    createElementVNode("div", _hoisted_2$e, [
      createElementVNode("div", _hoisted_3$c, toDisplayString(_ctx.title), 1),
      _ctx.hasContextMenu ? (openBlock(), createElementBlock("div", _hoisted_4$a, [
        createVNode(_component_vertical_dots, {
          class: "--clickable",
          onPointerdown: _cache[0] || (_cache[0] = withModifiers(() => {
          }, ["stop", "prevent"])),
          onClick: withModifiers(_ctx.openContextMenu, ["stop", "prevent"])
        }, null, 8, ["onClick"]),
        createVNode(_component_context_menu, {
          modelValue: _ctx.showContextMenu,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => _ctx.showContextMenu = $event),
          x: -100,
          y: 0,
          items: _ctx.contextMenuItems,
          onClick: _ctx.onContextMenuClick,
          onPointerdown: _cache[2] || (_cache[2] = withModifiers(() => {
          }, ["stop", "prevent"]))
        }, null, 8, ["modelValue", "items", "onClick"])
      ])) : createCommentVNode("", true)
    ])
  ], 8, _hoisted_1$j);
}
const PaletteEntry = /* @__PURE__ */ _export_sfc(_sfc_main$k, [["render", _sfc_render$k]]);
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  if (!getRandomValues) {
    getRandomValues = typeof crypto !== "undefined" && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);
    if (!getRandomValues) {
      throw new Error("crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported");
    }
  }
  return getRandomValues(rnds8);
}
const byteToHex = [];
for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 256).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
  return (byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + "-" + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + "-" + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + "-" + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + "-" + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]]).toLowerCase();
}
const randomUUID = typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID.bind(crypto);
const native = {
  randomUUID
};
function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }
  options = options || {};
  const rnds = options.random || (options.rng || rng)();
  rnds[6] = rnds[6] & 15 | 64;
  rnds[8] = rnds[8] & 63 | 128;
  if (buf) {
    offset = offset || 0;
    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }
    return buf;
  }
  return unsafeStringify(rnds);
}
const _sfc_main$j = defineComponent({
  props: {
    intf: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const onClick = () => {
      if (props.intf.callback) {
        props.intf.callback();
      }
    };
    return { onClick };
  }
});
const _hoisted_1$i = ["title"];
function _sfc_render$j(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("button", {
    class: "baklava-button --block",
    title: _ctx.intf.name,
    onClick: _cache[0] || (_cache[0] = (...args) => _ctx.onClick && _ctx.onClick(...args))
  }, toDisplayString(_ctx.intf.name), 9, _hoisted_1$i);
}
const ButtonInterfaceComponent = /* @__PURE__ */ _export_sfc(_sfc_main$j, [["render", _sfc_render$j]]);
class ButtonInterface extends NodeInterface$1 {
  constructor(name, callback) {
    super(name, void 0);
    this.component = markRaw(ButtonInterfaceComponent);
    this.callback = callback;
    this.setPort(false);
  }
}
const _sfc_main$i = defineComponent({
  props: {
    intf: {
      type: Object,
      required: true
    }
  }
});
const _hoisted_1$h = ["title"];
const _hoisted_2$d = /* @__PURE__ */ createElementVNode("div", { class: "__checkmark-container" }, [
  /* @__PURE__ */ createElementVNode("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "18",
    height: "18",
    viewBox: "0 0 18 18"
  }, [
    /* @__PURE__ */ createElementVNode("path", {
      class: "__checkmark",
      d: "M 6 5 L 6 10 L 16 10",
      transform: "rotate(-45 10 10)"
    })
  ])
], -1);
const _hoisted_3$b = { class: "__label" };
function _sfc_render$i(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["baklava-checkbox", { "--checked": _ctx.intf.value }]),
    title: _ctx.intf.name,
    onClick: _cache[0] || (_cache[0] = ($event) => _ctx.intf.value = !_ctx.intf.value)
  }, [
    _hoisted_2$d,
    createElementVNode("div", _hoisted_3$b, toDisplayString(_ctx.intf.name), 1)
  ], 10, _hoisted_1$h);
}
const CheckboxInterfaceComponent = /* @__PURE__ */ _export_sfc(_sfc_main$i, [["render", _sfc_render$i]]);
class CheckboxInterface extends NodeInterface$1 {
  constructor() {
    super(...arguments);
    this.component = markRaw(CheckboxInterfaceComponent);
  }
}
const MAX_STRING_LENGTH = 9;
function isValidator(intf) {
  return "validate" in intf;
}
class BaseNumericInterface extends NodeInterface$1 {
  constructor(name, value, min, max) {
    super(name, value);
    this.min = min;
    this.max = max;
  }
  validate(v) {
    return (!this.min || v >= this.min) && (!this.max || v <= this.max);
  }
}
const useBaseNumericInterface = (intf, precision = 3) => {
  const inputEl = ref(null);
  const editMode = ref(false);
  const invalid = ref(false);
  const tempValue = ref("0");
  const stringRepresentation = computed(() => {
    const s = intf.value.value.toFixed(precision);
    return s.length > MAX_STRING_LENGTH ? intf.value.value.toExponential(MAX_STRING_LENGTH - 5) : s;
  });
  const validate = (v) => {
    if (Number.isNaN(v)) {
      return false;
    } else if (isValidator(intf.value)) {
      return intf.value.validate(v);
    } else {
      return true;
    }
  };
  const setValue = (newValue) => {
    if (validate(newValue)) {
      intf.value.value = newValue;
    }
  };
  watch(tempValue, () => {
    invalid.value = false;
  });
  const enterEditMode = async () => {
    tempValue.value = intf.value.value.toFixed(precision);
    editMode.value = true;
    await nextTick();
    if (inputEl.value) {
      inputEl.value.focus();
    }
  };
  const leaveEditMode = () => {
    const v = parseFloat(tempValue.value);
    if (!validate(v)) {
      invalid.value = true;
    } else {
      setValue(v);
      editMode.value = false;
    }
  };
  return {
    editMode,
    invalid,
    tempValue,
    inputEl,
    stringRepresentation,
    validate,
    setValue,
    enterEditMode,
    leaveEditMode
  };
};
const _sfc_main$h = {};
const _hoisted_1$g = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2$c = /* @__PURE__ */ createElementVNode("polyline", { points: "6 9 12 15 18 9" }, null, -1);
const _hoisted_3$a = [
  _hoisted_2$c
];
function _sfc_render$h(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$g, _hoisted_3$a);
}
const Arrow = /* @__PURE__ */ _export_sfc(_sfc_main$h, [["render", _sfc_render$h]]);
const _sfc_main$g = defineComponent({
  components: {
    "i-arrow": Arrow
  },
  props: {
    intf: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const baseNumericInterface = useBaseNumericInterface(toRef(props, "intf"), 0);
    const increment = () => {
      baseNumericInterface.setValue(props.intf.value + 1);
    };
    const decrement = () => {
      baseNumericInterface.setValue(props.intf.value - 1);
    };
    return { ...baseNumericInterface, increment, decrement };
  }
});
const _hoisted_1$f = { class: "baklava-num-input" };
const _hoisted_2$b = ["title"];
const _hoisted_3$9 = { class: "__value" };
const _hoisted_4$9 = {
  key: 1,
  class: "__content"
};
function _sfc_render$g(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_i_arrow = resolveComponent("i-arrow");
  return openBlock(), createElementBlock("div", _hoisted_1$f, [
    createElementVNode("div", {
      class: "__button --dec",
      onClick: _cache[0] || (_cache[0] = (...args) => _ctx.decrement && _ctx.decrement(...args))
    }, [
      createVNode(_component_i_arrow)
    ]),
    !_ctx.editMode ? (openBlock(), createElementBlock("div", {
      key: 0,
      class: "__content",
      onClick: _cache[1] || (_cache[1] = (...args) => _ctx.enterEditMode && _ctx.enterEditMode(...args))
    }, [
      createElementVNode("div", {
        class: "__label",
        title: _ctx.intf.name
      }, toDisplayString(_ctx.intf.name), 9, _hoisted_2$b),
      createElementVNode("div", _hoisted_3$9, toDisplayString(_ctx.stringRepresentation), 1)
    ])) : (openBlock(), createElementBlock("div", _hoisted_4$9, [
      withDirectives(createElementVNode("input", {
        ref: "inputEl",
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => _ctx.tempValue = $event),
        type: "number",
        class: normalizeClass(["baklava-input", { "--invalid": _ctx.invalid }]),
        style: { "text-align": "right" },
        onBlur: _cache[3] || (_cache[3] = (...args) => _ctx.leaveEditMode && _ctx.leaveEditMode(...args)),
        onKeydown: _cache[4] || (_cache[4] = withKeys((...args) => _ctx.leaveEditMode && _ctx.leaveEditMode(...args), ["enter"]))
      }, null, 34), [
        [vModelText, _ctx.tempValue]
      ])
    ])),
    createElementVNode("div", {
      class: "__button --inc",
      onClick: _cache[5] || (_cache[5] = (...args) => _ctx.increment && _ctx.increment(...args))
    }, [
      createVNode(_component_i_arrow)
    ])
  ]);
}
const IntegerInterfaceComponent = /* @__PURE__ */ _export_sfc(_sfc_main$g, [["render", _sfc_render$g]]);
class IntegerInterface extends BaseNumericInterface {
  constructor() {
    super(...arguments);
    this.component = markRaw(IntegerInterfaceComponent);
  }
  validate(v) {
    return Number.isInteger(v) && super.validate(v);
  }
}
const _sfc_main$f = defineComponent({
  components: {
    "i-arrow": Arrow
  },
  props: {
    intf: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const baseNumericInterface = useBaseNumericInterface(toRef(props, "intf"));
    const increment = () => {
      baseNumericInterface.setValue(props.intf.value + 0.1);
    };
    const decrement = () => {
      baseNumericInterface.setValue(props.intf.value - 0.1);
    };
    return { ...baseNumericInterface, increment, decrement };
  }
});
const _hoisted_1$e = { class: "baklava-num-input" };
const _hoisted_2$a = ["title"];
const _hoisted_3$8 = { class: "__value" };
const _hoisted_4$8 = {
  key: 1,
  class: "__content"
};
function _sfc_render$f(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_i_arrow = resolveComponent("i-arrow");
  return openBlock(), createElementBlock("div", _hoisted_1$e, [
    createElementVNode("div", {
      class: "__button --dec",
      onClick: _cache[0] || (_cache[0] = (...args) => _ctx.decrement && _ctx.decrement(...args))
    }, [
      createVNode(_component_i_arrow)
    ]),
    !_ctx.editMode ? (openBlock(), createElementBlock("div", {
      key: 0,
      class: "__content",
      onClick: _cache[1] || (_cache[1] = (...args) => _ctx.enterEditMode && _ctx.enterEditMode(...args))
    }, [
      createElementVNode("div", {
        class: "__label",
        title: _ctx.intf.name
      }, toDisplayString(_ctx.intf.name), 9, _hoisted_2$a),
      createElementVNode("div", _hoisted_3$8, toDisplayString(_ctx.stringRepresentation), 1)
    ])) : (openBlock(), createElementBlock("div", _hoisted_4$8, [
      withDirectives(createElementVNode("input", {
        ref: "inputEl",
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => _ctx.tempValue = $event),
        type: "number",
        class: normalizeClass(["baklava-input", { "--invalid": _ctx.invalid }]),
        style: { "text-align": "right" },
        onBlur: _cache[3] || (_cache[3] = (...args) => _ctx.leaveEditMode && _ctx.leaveEditMode(...args)),
        onKeydown: _cache[4] || (_cache[4] = withKeys((...args) => _ctx.leaveEditMode && _ctx.leaveEditMode(...args), ["enter"]))
      }, null, 34), [
        [vModelText, _ctx.tempValue]
      ])
    ])),
    createElementVNode("div", {
      class: "__button --inc",
      onClick: _cache[5] || (_cache[5] = (...args) => _ctx.increment && _ctx.increment(...args))
    }, [
      createVNode(_component_i_arrow)
    ])
  ]);
}
const NumberInterfaceComponent = /* @__PURE__ */ _export_sfc(_sfc_main$f, [["render", _sfc_render$f]]);
class NumberInterface extends BaseNumericInterface {
  constructor() {
    super(...arguments);
    this.component = markRaw(NumberInterfaceComponent);
  }
}
const _sfc_main$e = defineComponent({
  components: {
    "i-arrow": Arrow
  },
  props: {
    intf: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const el = ref(null);
    const open = ref(false);
    const selectedItem = computed(
      () => props.intf.items.find(
        (v) => typeof v === "string" ? v === props.intf.value : v.value === props.intf.value
      )
    );
    const selectedText = computed(() => {
      if (selectedItem.value) {
        return typeof selectedItem.value === "string" ? selectedItem.value : selectedItem.value.text;
      } else {
        return "";
      }
    });
    const setSelected = (item) => {
      props.intf.value = typeof item === "string" ? item : item.value;
    };
    onClickOutside(el, () => {
      open.value = false;
    });
    return { el, open, selectedItem, selectedText, setSelected };
  }
});
const _hoisted_1$d = ["title"];
const _hoisted_2$9 = { class: "__selected" };
const _hoisted_3$7 = { class: "__text" };
const _hoisted_4$7 = { class: "__icon" };
const _hoisted_5$4 = { class: "__dropdown" };
const _hoisted_6$2 = { class: "item --header" };
const _hoisted_7 = ["onClick"];
function _sfc_render$e(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_i_arrow = resolveComponent("i-arrow");
  return openBlock(), createElementBlock("div", {
    ref: "el",
    class: normalizeClass(["baklava-select", { "--open": _ctx.open }]),
    title: _ctx.intf.name,
    onClick: _cache[0] || (_cache[0] = ($event) => _ctx.open = !_ctx.open)
  }, [
    createElementVNode("div", _hoisted_2$9, [
      createElementVNode("div", _hoisted_3$7, toDisplayString(_ctx.selectedText), 1),
      createElementVNode("div", _hoisted_4$7, [
        createVNode(_component_i_arrow)
      ])
    ]),
    createVNode(Transition, { name: "slide-fade" }, {
      default: withCtx(() => [
        withDirectives(createElementVNode("div", _hoisted_5$4, [
          createElementVNode("div", _hoisted_6$2, toDisplayString(_ctx.intf.name), 1),
          (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.intf.items, (item, i) => {
            return openBlock(), createElementBlock("div", {
              key: i,
              class: normalizeClass(["item", { "--active": item === _ctx.selectedItem }]),
              onClick: ($event) => _ctx.setSelected(item)
            }, toDisplayString(typeof item === "string" ? item : item.text), 11, _hoisted_7);
          }), 128))
        ], 512), [
          [vShow, _ctx.open]
        ])
      ]),
      _: 1
    })
  ], 10, _hoisted_1$d);
}
const SelectInterfaceComponent = /* @__PURE__ */ _export_sfc(_sfc_main$e, [["render", _sfc_render$e]]);
class SelectInterface extends NodeInterface$1 {
  constructor(name, value, items) {
    super(name, value);
    this.component = markRaw(SelectInterfaceComponent);
    this.items = items;
  }
}
const _sfc_main$d = defineComponent({
  props: {
    intf: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const el = ref(null);
    const baseNumericInterface = useBaseNumericInterface(toRef(props, "intf"));
    const didSlide = ref(false);
    const isMouseDown = ref(false);
    const percentage = computed(
      () => Math.min(100, Math.max(0, props.intf.value * 100 / (props.intf.max - props.intf.min)))
    );
    const mousedown = () => {
      if (baseNumericInterface.editMode.value) {
        return;
      }
      isMouseDown.value = true;
    };
    const mouseup = () => {
      if (baseNumericInterface.editMode.value) {
        return;
      }
      if (!didSlide.value) {
        void baseNumericInterface.enterEditMode();
      }
      isMouseDown.value = false;
      didSlide.value = false;
    };
    const mouseleave = (ev) => {
      if (baseNumericInterface.editMode.value) {
        return;
      }
      if (isMouseDown.value) {
        if (ev.offsetX >= el.value.clientWidth) {
          baseNumericInterface.setValue(props.intf.max);
        } else if (ev.offsetX <= 0) {
          baseNumericInterface.setValue(props.intf.min);
        }
      }
      isMouseDown.value = false;
      didSlide.value = false;
    };
    const mousemove = (ev) => {
      if (baseNumericInterface.editMode.value) {
        return;
      }
      const v = Math.max(
        props.intf.min,
        Math.min(
          props.intf.max,
          (props.intf.max - props.intf.min) * (ev.offsetX / el.value.clientWidth) + props.intf.min
        )
      );
      if (isMouseDown.value) {
        baseNumericInterface.setValue(v);
        didSlide.value = true;
      }
    };
    return { ...baseNumericInterface, el, percentage, mousedown, mouseup, mousemove, mouseleave };
  }
});
const _hoisted_1$c = {
  key: 0,
  class: "__content"
};
const _hoisted_2$8 = { class: "__label" };
const _hoisted_3$6 = { class: "__value" };
const _hoisted_4$6 = {
  key: 1,
  class: "__content"
};
function _sfc_render$d(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    ref: "el",
    class: normalizeClass(["baklava-slider", { "baklava-ignore-mouse": !_ctx.editMode }]),
    onPointerdown: _cache[3] || (_cache[3] = (...args) => _ctx.mousedown && _ctx.mousedown(...args)),
    onPointerup: _cache[4] || (_cache[4] = (...args) => _ctx.mouseup && _ctx.mouseup(...args)),
    onPointermove: _cache[5] || (_cache[5] = (...args) => _ctx.mousemove && _ctx.mousemove(...args)),
    onPointerleave: _cache[6] || (_cache[6] = (...args) => _ctx.mouseleave && _ctx.mouseleave(...args))
  }, [
    createElementVNode("div", {
      class: "__slider",
      style: normalizeStyle({ width: _ctx.percentage + "%" })
    }, null, 4),
    !_ctx.editMode ? (openBlock(), createElementBlock("div", _hoisted_1$c, [
      createElementVNode("div", _hoisted_2$8, toDisplayString(_ctx.intf.name), 1),
      createElementVNode("div", _hoisted_3$6, toDisplayString(_ctx.stringRepresentation), 1)
    ])) : (openBlock(), createElementBlock("div", _hoisted_4$6, [
      withDirectives(createElementVNode("input", {
        ref: "inputEl",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => _ctx.tempValue = $event),
        type: "number",
        class: normalizeClass(["baklava-input", { "--invalid": _ctx.invalid }]),
        style: { "text-align": "right" },
        onBlur: _cache[1] || (_cache[1] = (...args) => _ctx.leaveEditMode && _ctx.leaveEditMode(...args)),
        onKeydown: _cache[2] || (_cache[2] = withKeys((...args) => _ctx.leaveEditMode && _ctx.leaveEditMode(...args), ["enter"]))
      }, null, 34), [
        [vModelText, _ctx.tempValue]
      ])
    ]))
  ], 34);
}
const SliderInterfaceComponent = /* @__PURE__ */ _export_sfc(_sfc_main$d, [["render", _sfc_render$d]]);
class SliderInterface extends BaseNumericInterface {
  constructor(name, value, min, max) {
    super(name, value, min, max);
    this.component = markRaw(SliderInterfaceComponent);
    this.min = min;
    this.max = max;
  }
}
const _sfc_main$c = defineComponent({
  props: {
    intf: {
      type: Object,
      required: true
    }
  }
});
function _sfc_render$c(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", null, toDisplayString(_ctx.intf.value), 1);
}
const TextInterfaceComponent = /* @__PURE__ */ _export_sfc(_sfc_main$c, [["render", _sfc_render$c]]);
class TextInterface extends NodeInterface$1 {
  constructor(name, value) {
    super(name, value);
    this.component = markRaw(TextInterfaceComponent);
    this.setPort(false);
  }
}
const _sfc_main$b = defineComponent({
  props: {
    intf: {
      type: Object,
      required: true
    },
    modelValue: {
      type: String,
      required: true
    }
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const v = computed({
      get: () => props.modelValue,
      set: (v2) => {
        emit("update:modelValue", v2);
      }
    });
    return { v };
  }
});
const _hoisted_1$b = ["placeholder", "title"];
function _sfc_render$b(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", null, [
    withDirectives(createElementVNode("input", {
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => _ctx.v = $event),
      type: "text",
      class: "baklava-input",
      placeholder: _ctx.intf.name,
      title: _ctx.intf.name
    }, null, 8, _hoisted_1$b), [
      [vModelText, _ctx.v]
    ])
  ]);
}
const TextInputInterfaceComponent = /* @__PURE__ */ _export_sfc(_sfc_main$b, [["render", _sfc_render$b]]);
class TextInputInterface extends NodeInterface$1 {
  constructor() {
    super(...arguments);
    this.component = markRaw(TextInputInterfaceComponent);
  }
}
const SUBGRAPH_INPUT_NODE_TYPE = "__baklava_SubgraphInputNode";
const SUBGRAPH_OUTPUT_NODE_TYPE = "__baklava_SubgraphOutputNode";
const SubgraphInputNode = defineNode({
  type: SUBGRAPH_INPUT_NODE_TYPE,
  title: "Subgraph Input",
  inputs: {
    name: () => new TextInputInterface("Name", "Input").setPort(false)
  },
  outputs: {
    placeholder: () => new NodeInterface$1("Connection", void 0)
  },
  onCreate() {
    this.graphInterfaceId = v4();
  }
});
const SubgraphOutputNode = defineNode({
  type: SUBGRAPH_OUTPUT_NODE_TYPE,
  title: "Subgraph Output",
  inputs: {
    name: () => new TextInputInterface("Name", "Output").setPort(false),
    placeholder: () => new NodeInterface$1("Connection", void 0)
  },
  onCreate() {
    this.graphInterfaceId = v4();
  }
});
function checkRecursion(editor, currentGraph, graphNodeType) {
  if (!currentGraph.template) {
    return false;
  }
  if (getGraphNodeTypeString(currentGraph.template) === graphNodeType) {
    return true;
  }
  const template = editor.graphTemplates.find((t) => getGraphNodeTypeString(t) === graphNodeType);
  if (!template) {
    return false;
  }
  const containedGraphNodes = template.nodes.filter((n) => n.type.startsWith(GRAPH_NODE_TYPE_PREFIX));
  return containedGraphNodes.some((n) => checkRecursion(editor, currentGraph, n.type));
}
const _sfc_main$a = defineComponent({
  components: { PaletteEntry },
  setup() {
    const { viewModel } = useViewModel();
    const { x: mouseX, y: mouseY } = usePointer();
    const { transform } = useTransform();
    const editorEl = inject("editorEl");
    const draggedNode = ref(null);
    const categories = computed(() => {
      const nodeTypeEntries = Array.from(viewModel.value.editor.nodeTypes.entries());
      const categoryNames = new Set(nodeTypeEntries.map(([, ni]) => ni.category));
      const categories2 = [];
      for (const c of categoryNames.values()) {
        let nodeTypesInCategory = nodeTypeEntries.filter(([, ni]) => ni.category === c);
        if (viewModel.value.displayedGraph.template) {
          nodeTypesInCategory = nodeTypesInCategory.filter(
            ([nt]) => !checkRecursion(viewModel.value.editor, viewModel.value.displayedGraph, nt)
          );
        } else {
          nodeTypesInCategory = nodeTypesInCategory.filter(
            ([nt]) => ![SUBGRAPH_INPUT_NODE_TYPE, SUBGRAPH_OUTPUT_NODE_TYPE].includes(nt)
          );
        }
        if (nodeTypesInCategory.length > 0) {
          categories2.push({
            name: c,
            nodeTypes: Object.fromEntries(nodeTypesInCategory)
          });
        }
      }
      categories2.sort((a, b) => {
        if (a.name === "default") {
          return -1;
        } else if (b.name === "default") {
          return 1;
        } else {
          return a.name > b.name ? 1 : -1;
        }
      });
      return categories2;
    });
    const draggedNodeStyles = computed(() => {
      if (!draggedNode.value || !(editorEl == null ? void 0 : editorEl.value)) {
        return {};
      }
      const { left, top } = editorEl.value.getBoundingClientRect();
      return {
        top: `${mouseY.value - top}px`,
        left: `${mouseX.value - left}px`
      };
    });
    const onDragStart = (type, nodeInformation) => {
      draggedNode.value = {
        type,
        nodeInformation
      };
      const onDragEnd = () => {
        const instance = reactive(new nodeInformation.type());
        viewModel.value.displayedGraph.addNode(instance);
        const rect = editorEl.value.getBoundingClientRect();
        const [x, y] = transform(mouseX.value - rect.left, mouseY.value - rect.top);
        instance.position.x = x;
        instance.position.y = y;
        draggedNode.value = null;
        document.removeEventListener("pointerup", onDragEnd);
      };
      document.addEventListener("pointerup", onDragEnd);
    };
    return { draggedNode, categories, draggedNodeStyles, onDragStart, mouseX, mouseY };
  }
});
const _hoisted_1$a = { class: "baklava-node-palette" };
const _hoisted_2$7 = { key: 0 };
function _sfc_render$a(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_PaletteEntry = resolveComponent("PaletteEntry");
  return openBlock(), createElementBlock(Fragment, null, [
    createElementVNode("div", _hoisted_1$a, [
      (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.categories, (c) => {
        return openBlock(), createElementBlock("section", {
          key: c.name
        }, [
          c.name !== "default" ? (openBlock(), createElementBlock("h1", _hoisted_2$7, toDisplayString(c.name), 1)) : createCommentVNode("", true),
          (openBlock(true), createElementBlock(Fragment, null, renderList(c.nodeTypes, (ni, nt) => {
            return openBlock(), createBlock(_component_PaletteEntry, {
              key: nt,
              type: nt,
              title: ni.title,
              onPointerdown: ($event) => _ctx.onDragStart(nt, ni)
            }, null, 8, ["type", "title", "onPointerdown"]);
          }), 128))
        ]);
      }), 128))
    ]),
    createVNode(Transition, { name: "fade" }, {
      default: withCtx(() => [
        _ctx.draggedNode ? (openBlock(), createElementBlock("div", {
          key: 0,
          class: "baklava-dragged-node",
          style: normalizeStyle(_ctx.draggedNodeStyles)
        }, [
          createVNode(_component_PaletteEntry, {
            type: _ctx.draggedNode.type,
            title: _ctx.draggedNode.nodeInformation.title
          }, null, 8, ["type", "title"])
        ], 4)) : createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64);
}
const NodePalette = /* @__PURE__ */ _export_sfc(_sfc_main$a, [["render", _sfc_render$a]]);
const SAVE_SUBGRAPH_COMMAND = "SAVE_SUBGRAPH";
function registerSaveSubgraphCommand(displayedGraph, handler) {
  const saveSubgraph = () => {
    const graph = displayedGraph.value;
    if (!graph.template) {
      throw new Error("Graph template property not set");
    }
    const interfaceConnections = [];
    const inputs = [];
    const inputNodes = graph.nodes.filter((n) => n.type === SUBGRAPH_INPUT_NODE_TYPE);
    for (const n of inputNodes) {
      const connections = graph.connections.filter((c) => c.from === n.outputs.placeholder);
      connections.forEach((c) => {
        inputs.push({
          id: n.graphInterfaceId,
          name: n.inputs.name.value,
          nodeInterfaceId: c.to.id
        });
      });
      interfaceConnections.push(...connections);
    }
    const outputs = [];
    const outputNodes = graph.nodes.filter((n) => n.type === SUBGRAPH_OUTPUT_NODE_TYPE);
    for (const n of outputNodes) {
      const connections = graph.connections.filter((c) => c.to === n.inputs.placeholder);
      connections.forEach((c) => {
        outputs.push({
          id: n.graphInterfaceId,
          name: n.inputs.name.value,
          nodeInterfaceId: c.from.id
        });
      });
      interfaceConnections.push(...connections);
    }
    const innerConnections = graph.connections.filter((c) => !interfaceConnections.includes(c));
    const nodes = graph.nodes.filter(
      (n) => n.type !== SUBGRAPH_INPUT_NODE_TYPE && n.type !== SUBGRAPH_OUTPUT_NODE_TYPE
    );
    graph.template.update({
      inputs,
      outputs,
      connections: innerConnections.map((c) => ({ id: c.id, from: c.from.id, to: c.to.id })),
      nodes: nodes.map((n) => n.save())
    });
    graph.template.panning = graph.panning;
    graph.template.scaling = graph.scaling;
  };
  handler.registerCommand(SAVE_SUBGRAPH_COMMAND, {
    canExecute: () => {
      var _a2;
      return displayedGraph.value !== ((_a2 = displayedGraph.value.editor) == null ? void 0 : _a2.graph);
    },
    execute: saveSubgraph
  });
}
const CREATE_SUBGRAPH_COMMAND = "CREATE_SUBGRAPH";
const IGNORE_NODE_TYPES = [SUBGRAPH_INPUT_NODE_TYPE, SUBGRAPH_OUTPUT_NODE_TYPE];
function registerCreateSubgraphCommand(displayedGraph, handler, switchGraph) {
  const canCreateSubgraph = () => {
    return displayedGraph.value.selectedNodes.filter((n) => !IGNORE_NODE_TYPES.includes(n.type)).length > 0;
  };
  const createSubgraph = () => {
    const graph = displayedGraph.value;
    const editor = displayedGraph.value.editor;
    if (graph.selectedNodes.length === 0) {
      return;
    }
    const selectedNodes = graph.selectedNodes.filter((n) => !IGNORE_NODE_TYPES.includes(n.type));
    const selectedNodesInputs = selectedNodes.flatMap((n) => Object.values(n.inputs));
    const selectedNodesOutputs = selectedNodes.flatMap((n) => Object.values(n.outputs));
    const inputConnections = graph.connections.filter(
      (c) => !selectedNodesOutputs.includes(c.from) && selectedNodesInputs.includes(c.to)
    );
    const outputConnections = graph.connections.filter(
      (c) => selectedNodesOutputs.includes(c.from) && !selectedNodesInputs.includes(c.to)
    );
    const innerConnections = graph.connections.filter(
      (c) => selectedNodesOutputs.includes(c.from) && selectedNodesInputs.includes(c.to)
    );
    const inputInterfaces = inputConnections.map((c) => c.to);
    const outputInterfaces = outputConnections.map((c) => c.from);
    const interfaceIdMap = /* @__PURE__ */ new Map();
    const graphInputs = [];
    for (const i of inputInterfaces) {
      const newId = v4();
      interfaceIdMap.set(i.id, newId);
      graphInputs.push({ id: newId, nodeInterfaceId: i.id, name: i.name });
    }
    const graphOutputs = [];
    for (const i of outputInterfaces) {
      const newId = v4();
      interfaceIdMap.set(i.id, newId);
      graphOutputs.push({ id: newId, nodeInterfaceId: i.id, name: i.name });
    }
    const subgraphTemplate = reactive(
      new GraphTemplate(
        {
          connections: innerConnections.map((c) => ({ id: c.id, from: c.from.id, to: c.to.id })),
          inputs: graphInputs,
          outputs: graphOutputs,
          nodes: selectedNodes.map((n) => n.save())
        },
        editor
      )
    );
    editor.addGraphTemplate(subgraphTemplate);
    const nt = editor.nodeTypes.get(getGraphNodeTypeString(subgraphTemplate));
    if (!nt) {
      throw new Error("Unable to create subgraph: Could not find corresponding graph node type");
    }
    const node = reactive(new nt.type());
    graph.addNode(node);
    const averageX = Math.round(
      selectedNodes.map((n) => n.position.x).reduce((p, c) => p + c, 0) / selectedNodes.length
    );
    const averageY = Math.round(
      selectedNodes.map((n) => n.position.y).reduce((p, c) => p + c, 0) / selectedNodes.length
    );
    node.position.x = averageX;
    node.position.y = averageY;
    inputConnections.forEach((c) => {
      graph.removeConnection(c);
      graph.addConnection(c.from, node.inputs[interfaceIdMap.get(c.to.id)]);
    });
    outputConnections.forEach((c) => {
      graph.removeConnection(c);
      graph.addConnection(node.outputs[interfaceIdMap.get(c.from.id)], c.to);
    });
    selectedNodes.forEach((n) => graph.removeNode(n));
    if (handler.canExecuteCommand(SAVE_SUBGRAPH_COMMAND)) {
      handler.executeCommand(SAVE_SUBGRAPH_COMMAND);
    }
    switchGraph(subgraphTemplate);
    displayedGraph.value.panning = { ...graph.panning };
    displayedGraph.value.scaling = graph.scaling;
  };
  handler.registerCommand(CREATE_SUBGRAPH_COMMAND, {
    canExecute: canCreateSubgraph,
    execute: createSubgraph
  });
}
const DELETE_NODES_COMMAND = "DELETE_NODES";
function registerDeleteNodesCommand(displayedGraph, handler) {
  handler.registerCommand(DELETE_NODES_COMMAND, {
    canExecute: () => displayedGraph.value.selectedNodes.length > 0,
    execute() {
      displayedGraph.value.selectedNodes.forEach((n) => displayedGraph.value.removeNode(n));
    }
  });
  handler.registerHotkey(["Delete"], DELETE_NODES_COMMAND);
}
const SWITCH_TO_MAIN_GRAPH_COMMAND = "SWITCH_TO_MAIN_GRAPH";
function registerSwitchToMainGraphCommand(displayedGraph, handler, switchGraph) {
  handler.registerCommand(SWITCH_TO_MAIN_GRAPH_COMMAND, {
    canExecute: () => displayedGraph.value !== displayedGraph.value.editor.graph,
    execute: () => {
      handler.executeCommand(SAVE_SUBGRAPH_COMMAND);
      switchGraph(displayedGraph.value.editor.graph);
    }
  });
}
function registerGraphCommands(displayedGraph, handler, switchGraph) {
  registerDeleteNodesCommand(displayedGraph, handler);
  registerCreateSubgraphCommand(displayedGraph, handler, switchGraph);
  registerSaveSubgraphCommand(displayedGraph, handler);
  registerSwitchToMainGraphCommand(displayedGraph, handler, switchGraph);
}
class NodeStep {
  constructor(type, data) {
    this.type = type;
    if (type === "addNode") {
      this.nodeId = data;
    } else {
      this.nodeState = data;
    }
  }
  undo(graph) {
    if (this.type === "addNode") {
      this.removeNode(graph);
    } else {
      this.addNode(graph);
    }
  }
  redo(graph) {
    if (this.type === "addNode" && this.nodeState) {
      this.addNode(graph);
    } else if (this.type === "removeNode" && this.nodeId) {
      this.removeNode(graph);
    }
  }
  addNode(graph) {
    const nodeType = graph.editor.nodeTypes.get(this.nodeState.type);
    if (!nodeType) {
      return;
    }
    const n = new nodeType.type();
    graph.addNode(n);
    n.load(this.nodeState);
    this.nodeId = n.id;
  }
  removeNode(graph) {
    const node = graph.nodes.find((n) => n.id === this.nodeId);
    if (!node) {
      return;
    }
    this.nodeState = node.save();
    graph.removeNode(node);
  }
}
class ConnectionStep {
  constructor(type, data) {
    this.type = type;
    if (type === "addConnection") {
      this.connectionId = data;
    } else {
      const d = data;
      this.connectionState = {
        id: d.id,
        from: d.from.id,
        to: d.to.id
      };
    }
  }
  undo(graph) {
    if (this.type === "addConnection") {
      this.removeConnection(graph);
    } else {
      this.addConnection(graph);
    }
  }
  redo(graph) {
    if (this.type === "addConnection" && this.connectionState) {
      this.addConnection(graph);
    } else if (this.type === "removeConnection" && this.connectionId) {
      this.removeConnection(graph);
    }
  }
  addConnection(graph) {
    const fromIntf = graph.findNodeInterface(this.connectionState.from);
    const toIntf = graph.findNodeInterface(this.connectionState.to);
    if (!fromIntf || !toIntf) {
      return;
    }
    graph.addConnection(fromIntf, toIntf);
  }
  removeConnection(graph) {
    const connection = graph.connections.find((c) => c.id === this.connectionId);
    if (!connection) {
      return;
    }
    this.connectionState = {
      id: connection.id,
      from: connection.from.id,
      to: connection.to.id
    };
    graph.removeConnection(connection);
  }
}
class TransactionStep {
  constructor(steps) {
    this.type = "transaction";
    if (steps.length === 0) {
      throw new Error("Can't create a transaction with no steps");
    }
    this.steps = steps;
  }
  undo(graph) {
    for (let i = this.steps.length - 1; i >= 0; i--) {
      this.steps[i].undo(graph);
    }
  }
  redo(graph) {
    for (let i = 0; i < this.steps.length; i++) {
      this.steps[i].redo(graph);
    }
  }
}
const UNDO_COMMAND = "UNDO";
const REDO_COMMAND = "REDO";
const START_TRANSACTION_COMMAND = "START_TRANSACTION";
const COMMIT_TRANSACTION_COMMAND = "COMMIT_TRANSACTION";
function useHistory(graph, commandHandler) {
  const token = Symbol("HistoryToken");
  const maxSteps = ref(200);
  const steps = ref([]);
  const changeBySelf = ref(false);
  const currentIndex = ref(-1);
  const activeTransaction = ref(false);
  const transactionSteps = ref([]);
  const addStep = (step) => {
    if (changeBySelf.value) {
      return;
    }
    if (activeTransaction.value) {
      transactionSteps.value.push(step);
    } else {
      if (currentIndex.value !== steps.value.length - 1) {
        steps.value = steps.value.slice(0, currentIndex.value + 1);
      }
      steps.value.push(step);
      currentIndex.value++;
      while (steps.value.length > maxSteps.value) {
        steps.value.shift();
      }
    }
  };
  const startTransaction = () => {
    activeTransaction.value = true;
  };
  const commitTransaction = () => {
    activeTransaction.value = false;
    if (transactionSteps.value.length > 0) {
      addStep(new TransactionStep(transactionSteps.value));
      transactionSteps.value = [];
    }
  };
  const canUndo = () => steps.value.length !== 0 && currentIndex.value !== -1;
  const undo = () => {
    if (!canUndo()) {
      return;
    }
    changeBySelf.value = true;
    steps.value[currentIndex.value--].undo(graph.value);
    changeBySelf.value = false;
  };
  const canRedo = () => steps.value.length !== 0 && currentIndex.value < steps.value.length - 1;
  const redo = () => {
    if (!canRedo()) {
      return;
    }
    changeBySelf.value = true;
    steps.value[++currentIndex.value].redo(graph.value);
    changeBySelf.value = false;
  };
  watch(
    graph,
    (newGraph, oldGraph) => {
      if (oldGraph) {
        oldGraph.events.addNode.unsubscribe(token);
        oldGraph.events.removeNode.unsubscribe(token);
        oldGraph.events.addConnection.unsubscribe(token);
        oldGraph.events.removeConnection.unsubscribe(token);
      }
      if (newGraph) {
        newGraph.events.addNode.subscribe(token, (node) => {
          addStep(new NodeStep("addNode", node.id));
        });
        newGraph.events.removeNode.subscribe(token, (node) => {
          addStep(new NodeStep("removeNode", node.save()));
        });
        newGraph.events.addConnection.subscribe(token, (conn) => {
          addStep(new ConnectionStep("addConnection", conn.id));
        });
        newGraph.events.removeConnection.subscribe(token, (conn) => {
          addStep(new ConnectionStep("removeConnection", conn));
        });
      }
    },
    { immediate: true }
  );
  commandHandler.registerCommand(UNDO_COMMAND, {
    canExecute: canUndo,
    execute: undo
  });
  commandHandler.registerCommand(REDO_COMMAND, {
    canExecute: canRedo,
    execute: redo
  });
  commandHandler.registerCommand(START_TRANSACTION_COMMAND, {
    canExecute: () => !activeTransaction.value,
    execute: startTransaction
  });
  commandHandler.registerCommand(COMMIT_TRANSACTION_COMMAND, {
    canExecute: () => activeTransaction.value,
    execute: commitTransaction
  });
  commandHandler.registerHotkey(["Control", "z"], UNDO_COMMAND);
  commandHandler.registerHotkey(["Control", "y"], REDO_COMMAND);
  return reactive({
    maxSteps
  });
}
const COPY_COMMAND = "COPY";
const PASTE_COMMAND = "PASTE";
const CLEAR_CLIPBOARD_COMMAND = "CLEAR_CLIPBOARD";
function useClipboard(displayedGraph, editor, commandHandler) {
  const token = Symbol("ClipboardToken");
  const nodeBuffer = ref("");
  const connectionBuffer = ref("");
  const isEmpty = computed(() => !nodeBuffer.value);
  const clear = () => {
    nodeBuffer.value = "";
    connectionBuffer.value = "";
  };
  const copy = () => {
    const interfacesOfSelectedNodes = displayedGraph.value.selectedNodes.flatMap((n) => [
      ...Object.values(n.inputs),
      ...Object.values(n.outputs)
    ]);
    const connections = displayedGraph.value.connections.filter(
      (conn) => interfacesOfSelectedNodes.includes(conn.from) || interfacesOfSelectedNodes.includes(conn.to)
    ).map((conn) => ({ from: conn.from.id, to: conn.to.id }));
    connectionBuffer.value = JSON.stringify(connections);
    nodeBuffer.value = JSON.stringify(displayedGraph.value.selectedNodes.map((n) => n.save()));
  };
  const findInterface = (nodes, id, io) => {
    for (const n of nodes) {
      let intf;
      if (!io || io === "input") {
        intf = Object.values(n.inputs).find((intf2) => intf2.id === id);
      }
      if (!intf && (!io || io === "output")) {
        intf = Object.values(n.outputs).find((intf2) => intf2.id === id);
      }
      if (intf) {
        return intf;
      }
    }
    return void 0;
  };
  const paste = () => {
    if (isEmpty.value) {
      return;
    }
    const idmap = /* @__PURE__ */ new Map();
    const parsedNodeBuffer = JSON.parse(nodeBuffer.value);
    const parsedConnectionBuffer = JSON.parse(connectionBuffer.value);
    const newNodes = [];
    const newConnections = [];
    const graph = displayedGraph.value;
    commandHandler.executeCommand(START_TRANSACTION_COMMAND);
    for (const n of parsedNodeBuffer) {
      const nodeType = editor.value.nodeTypes.get(n.type);
      if (!nodeType) {
        console.warn(`Node type ${n.type} not registered`);
        return;
      }
      const copiedNode = new nodeType.type();
      const generatedId = copiedNode.id;
      newNodes.push(copiedNode);
      const tapInterfaces = (intfs) => {
        Object.values(intfs).forEach((intf) => {
          intf.hooks.load.subscribe(token, (intfState) => {
            const newIntfId = v4();
            idmap.set(intfState.id, newIntfId);
            intf.id = newIntfId;
            intf.hooks.load.unsubscribe(token);
            return intfState;
          });
        });
      };
      tapInterfaces(copiedNode.inputs);
      tapInterfaces(copiedNode.outputs);
      copiedNode.hooks.beforeLoad.subscribe(token, (nodeState) => {
        const ns = nodeState;
        if (ns.position) {
          ns.position.x += 10;
          ns.position.y += 10;
        }
        copiedNode.hooks.beforeLoad.unsubscribe(token);
        return ns;
      });
      graph.addNode(copiedNode);
      copiedNode.load(n);
      copiedNode.id = generatedId;
      idmap.set(n.id, generatedId);
    }
    for (const c of parsedConnectionBuffer) {
      const fromIntf = findInterface(newNodes, idmap.get(c.from), "output");
      const toIntf = findInterface(newNodes, idmap.get(c.to), "input");
      if (!fromIntf || !toIntf) {
        continue;
      }
      const newConnection = graph.addConnection(fromIntf, toIntf);
      if (newConnection) {
        newConnections.push(newConnection);
      }
    }
    commandHandler.executeCommand(COMMIT_TRANSACTION_COMMAND);
    return {
      newNodes,
      newConnections
    };
  };
  commandHandler.registerCommand(COPY_COMMAND, {
    canExecute: () => true,
    execute: copy
  });
  commandHandler.registerHotkey(["Control", "c"], COPY_COMMAND);
  commandHandler.registerCommand(PASTE_COMMAND, {
    canExecute: () => !isEmpty.value,
    execute: paste
  });
  commandHandler.registerHotkey(["Control", "v"], PASTE_COMMAND);
  commandHandler.registerCommand(CLEAR_CLIPBOARD_COMMAND, {
    canExecute: () => true,
    execute: clear
  });
  return reactive({ isEmpty });
}
const OPEN_SIDEBAR_COMMAND = "OPEN_SIDEBAR";
function registerOpenSidebarCommand(displayedGraph, handler) {
  handler.registerCommand(OPEN_SIDEBAR_COMMAND, {
    execute: (nodeId) => {
      displayedGraph.value.sidebar.nodeId = nodeId;
      displayedGraph.value.sidebar.visible = true;
    },
    canExecute: () => true
  });
}
function registerSidebarCommands(displayedGraph, handler) {
  registerOpenSidebarCommand(displayedGraph, handler);
}
const commandList = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  CREATE_SUBGRAPH_COMMAND,
  DELETE_NODES_COMMAND,
  SAVE_SUBGRAPH_COMMAND,
  SWITCH_TO_MAIN_GRAPH_COMMAND,
  COMMIT_TRANSACTION_COMMAND,
  START_TRANSACTION_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  CLEAR_CLIPBOARD_COMMAND,
  COPY_COMMAND,
  PASTE_COMMAND,
  OPEN_SIDEBAR_COMMAND
}, Symbol.toStringTag, { value: "Module" }));
const _sfc_main$9 = {};
const _hoisted_1$9 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2$6 = /* @__PURE__ */ createElementVNode("path", {
  stroke: "none",
  d: "M0 0h24v24H0z",
  fill: "none"
}, null, -1);
const _hoisted_3$5 = /* @__PURE__ */ createElementVNode("path", { d: "M9 13l-4 -4l4 -4m-4 4h11a4 4 0 0 1 0 8h-1" }, null, -1);
const _hoisted_4$5 = [
  _hoisted_2$6,
  _hoisted_3$5
];
function _sfc_render$9(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$9, _hoisted_4$5);
}
const ArrowBackUp = /* @__PURE__ */ _export_sfc(_sfc_main$9, [["render", _sfc_render$9]]);
const _sfc_main$8 = {};
const _hoisted_1$8 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2$5 = /* @__PURE__ */ createElementVNode("path", {
  stroke: "none",
  d: "M0 0h24v24H0z",
  fill: "none"
}, null, -1);
const _hoisted_3$4 = /* @__PURE__ */ createElementVNode("path", { d: "M15 13l4 -4l-4 -4m4 4h-11a4 4 0 0 0 0 8h1" }, null, -1);
const _hoisted_4$4 = [
  _hoisted_2$5,
  _hoisted_3$4
];
function _sfc_render$8(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$8, _hoisted_4$4);
}
const ArrowForwardUp = /* @__PURE__ */ _export_sfc(_sfc_main$8, [["render", _sfc_render$8]]);
const _sfc_main$7 = {};
const _hoisted_1$7 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2$4 = /* @__PURE__ */ createElementVNode("path", {
  stroke: "none",
  d: "M0 0h24v24H0z",
  fill: "none"
}, null, -1);
const _hoisted_3$3 = /* @__PURE__ */ createElementVNode("line", {
  x1: "5",
  y1: "12",
  x2: "19",
  y2: "12"
}, null, -1);
const _hoisted_4$3 = /* @__PURE__ */ createElementVNode("line", {
  x1: "5",
  y1: "12",
  x2: "11",
  y2: "18"
}, null, -1);
const _hoisted_5$3 = /* @__PURE__ */ createElementVNode("line", {
  x1: "5",
  y1: "12",
  x2: "11",
  y2: "6"
}, null, -1);
const _hoisted_6$1 = [
  _hoisted_2$4,
  _hoisted_3$3,
  _hoisted_4$3,
  _hoisted_5$3
];
function _sfc_render$7(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$7, _hoisted_6$1);
}
const ArrowLeft = /* @__PURE__ */ _export_sfc(_sfc_main$7, [["render", _sfc_render$7]]);
const _sfc_main$6 = {};
const _hoisted_1$6 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2$3 = /* @__PURE__ */ createElementVNode("path", {
  stroke: "none",
  d: "M0 0h24v24H0z",
  fill: "none"
}, null, -1);
const _hoisted_3$2 = /* @__PURE__ */ createElementVNode("path", { d: "M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" }, null, -1);
const _hoisted_4$2 = /* @__PURE__ */ createElementVNode("rect", {
  x: "9",
  y: "3",
  width: "6",
  height: "4",
  rx: "2"
}, null, -1);
const _hoisted_5$2 = [
  _hoisted_2$3,
  _hoisted_3$2,
  _hoisted_4$2
];
function _sfc_render$6(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$6, _hoisted_5$2);
}
const Clipboard = /* @__PURE__ */ _export_sfc(_sfc_main$6, [["render", _sfc_render$6]]);
const _sfc_main$5 = {};
const _hoisted_1$5 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2$2 = /* @__PURE__ */ createElementVNode("path", {
  stroke: "none",
  d: "M0 0h24v24H0z",
  fill: "none"
}, null, -1);
const _hoisted_3$1 = /* @__PURE__ */ createElementVNode("rect", {
  x: "8",
  y: "8",
  width: "12",
  height: "12",
  rx: "2"
}, null, -1);
const _hoisted_4$1 = /* @__PURE__ */ createElementVNode("path", { d: "M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2" }, null, -1);
const _hoisted_5$1 = [
  _hoisted_2$2,
  _hoisted_3$1,
  _hoisted_4$1
];
function _sfc_render$5(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$5, _hoisted_5$1);
}
const Copy = /* @__PURE__ */ _export_sfc(_sfc_main$5, [["render", _sfc_render$5]]);
const _sfc_main$4 = {};
const _hoisted_1$4 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2$1 = /* @__PURE__ */ createElementVNode("path", {
  stroke: "none",
  d: "M0 0h24v24H0z",
  fill: "none"
}, null, -1);
const _hoisted_3 = /* @__PURE__ */ createElementVNode("path", { d: "M6 4h10l4 4v10a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2" }, null, -1);
const _hoisted_4 = /* @__PURE__ */ createElementVNode("circle", {
  cx: "12",
  cy: "14",
  r: "2"
}, null, -1);
const _hoisted_5 = /* @__PURE__ */ createElementVNode("polyline", { points: "14 4 14 8 8 8 8 4" }, null, -1);
const _hoisted_6 = [
  _hoisted_2$1,
  _hoisted_3,
  _hoisted_4,
  _hoisted_5
];
function _sfc_render$4(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$4, _hoisted_6);
}
const DeviceFloppy = /* @__PURE__ */ _export_sfc(_sfc_main$4, [["render", _sfc_render$4]]);
const _sfc_main$3 = {};
const _hoisted_1$3 = {
  xmlns: "http://www.w3.org/2000/svg",
  class: "baklava-icon",
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "stroke-width": "2",
  stroke: "currentColor",
  fill: "none",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
};
const _hoisted_2 = /* @__PURE__ */ createStaticVNode('<path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M10 3h4v4h-4z"></path><path d="M3 17h4v4h-4z"></path><path d="M17 17h4v4h-4z"></path><path d="M7 17l5 -4l5 4"></path><line x1="12" y1="7" x2="12" y2="13"></line>', 6);
const _hoisted_8 = [
  _hoisted_2
];
function _sfc_render$3(_ctx, _cache) {
  return openBlock(), createElementBlock("svg", _hoisted_1$3, _hoisted_8);
}
const Hierarchy2 = /* @__PURE__ */ _export_sfc(_sfc_main$3, [["render", _sfc_render$3]]);
const _sfc_main$2 = defineComponent({
  props: {
    command: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    icon: {
      type: Object,
      required: false,
      default: void 0
    }
  },
  setup() {
    const { viewModel } = useViewModel();
    return { viewModel };
  }
});
const _hoisted_1$2 = ["disabled", "title"];
function _sfc_render$2(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("button", {
    class: "baklava-toolbar-entry baklava-toolbar-button",
    disabled: !_ctx.viewModel.commandHandler.canExecuteCommand(_ctx.command),
    title: _ctx.title,
    onClick: _cache[0] || (_cache[0] = ($event) => _ctx.viewModel.commandHandler.executeCommand(_ctx.command))
  }, [
    _ctx.icon ? (openBlock(), createBlock(resolveDynamicComponent(_ctx.icon), { key: 0 })) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
      createTextVNode(toDisplayString(_ctx.title), 1)
    ], 64))
  ], 8, _hoisted_1$2);
}
const ToolbarButton = /* @__PURE__ */ _export_sfc(_sfc_main$2, [["render", _sfc_render$2]]);
const _sfc_main$1 = defineComponent({
  components: { ToolbarButton },
  setup() {
    const { viewModel } = useViewModel();
    const isSubgraph = computed(() => viewModel.value.displayedGraph !== viewModel.value.editor.graph);
    const commands = [
      { command: COPY_COMMAND, title: "Copy", icon: Copy },
      { command: PASTE_COMMAND, title: "Paste", icon: Clipboard },
      { command: UNDO_COMMAND, title: "Undo", icon: ArrowBackUp },
      { command: REDO_COMMAND, title: "Redo", icon: ArrowForwardUp },
      { command: CREATE_SUBGRAPH_COMMAND, title: "Create Subgraph", icon: Hierarchy2 }
    ];
    const subgraphCommands = [
      { command: SAVE_SUBGRAPH_COMMAND, title: "Save Subgraph", icon: DeviceFloppy },
      { command: SWITCH_TO_MAIN_GRAPH_COMMAND, title: "Back to Main Graph", icon: ArrowLeft }
    ];
    return { isSubgraph, commands, subgraphCommands };
  }
});
const _hoisted_1$1 = { class: "baklava-toolbar" };
function _sfc_render$1(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_toolbar_button = resolveComponent("toolbar-button");
  return openBlock(), createElementBlock("div", _hoisted_1$1, [
    (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.commands, (c) => {
      return openBlock(), createBlock(_component_toolbar_button, {
        key: c.command,
        command: c.command,
        title: c.title,
        icon: c.icon
      }, null, 8, ["command", "title", "icon"]);
    }), 128)),
    _ctx.isSubgraph ? (openBlock(true), createElementBlock(Fragment, { key: 0 }, renderList(_ctx.subgraphCommands, (c) => {
      return openBlock(), createBlock(_component_toolbar_button, {
        key: c.command,
        command: c.command,
        title: c.title,
        icon: c.icon
      }, null, 8, ["command", "title", "icon"]);
    }), 128)) : createCommentVNode("", true)
  ]);
}
const Toolbar = /* @__PURE__ */ _export_sfc(_sfc_main$1, [["render", _sfc_render$1]]);
const _sfc_main = defineComponent({
  components: { Background, Node: _sfc_main$q, ConnectionWrapper, TemporaryConnection, Sidebar, Minimap, NodePalette, Toolbar },
  props: {
    viewModel: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const token = Symbol("EditorToken");
    const viewModelRef2 = toRef(props, "viewModel");
    providePlugin(viewModelRef2);
    const el = ref(null);
    provide("editorEl", el);
    const nodes = computed(() => props.viewModel.displayedGraph.nodes);
    const connections = computed(() => props.viewModel.displayedGraph.connections);
    const selectedNodes = computed(() => props.viewModel.displayedGraph.selectedNodes);
    const panZoom = usePanZoom();
    const temporaryConnection = useTemporaryConnection();
    const nodeContainerStyle = computed(() => ({
      ...panZoom.styles.value
    }));
    const counter = ref(0);
    props.viewModel.editor.hooks.load.subscribe(token, (s) => {
      counter.value++;
      return s;
    });
    const onPointerMove = (ev) => {
      panZoom.onPointerMove(ev);
      temporaryConnection.onMouseMove(ev);
    };
    const onPointerDown = (ev) => {
      if (ev.button === 0) {
        if (ev.target === el.value) {
          unselectAllNodes();
          panZoom.onPointerDown(ev);
        }
        temporaryConnection.onMouseDown();
      }
    };
    const onPointerUp = (ev) => {
      panZoom.onPointerUp(ev);
      temporaryConnection.onMouseUp();
    };
    const keyDown = (ev) => {
      if (ev.key === "Tab") {
        ev.preventDefault();
      }
      props.viewModel.commandHandler.handleKeyDown(ev);
    };
    const keyUp = (ev) => {
      props.viewModel.commandHandler.handleKeyUp(ev);
    };
    const selectNode = (node) => {
      if (!props.viewModel.commandHandler.pressedKeys.includes("Control")) {
        unselectAllNodes();
      }
      props.viewModel.displayedGraph.selectedNodes.push(node);
    };
    const unselectAllNodes = () => {
      props.viewModel.displayedGraph.selectedNodes = [];
    };
    return {
      el,
      counter,
      nodes,
      connections,
      selectedNodes,
      nodeContainerStyle,
      onPointerMove,
      onPointerDown,
      onPointerUp,
      keyDown,
      keyUp,
      selectNode,
      temporaryConnection: temporaryConnection.temporaryConnection,
      mouseWheel: panZoom.onMouseWheel,
      dragging: panZoom.dragging
    };
  }
});
const _hoisted_1 = { class: "connections-container" };
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_background = resolveComponent("background");
  const _component_toolbar = resolveComponent("toolbar");
  const _component_node_palette = resolveComponent("node-palette");
  const _component_connection_wrapper = resolveComponent("connection-wrapper");
  const _component_temporary_connection = resolveComponent("temporary-connection");
  const _component_node = resolveComponent("node");
  const _component_sidebar = resolveComponent("sidebar");
  const _component_minimap = resolveComponent("minimap");
  return openBlock(), createElementBlock("div", {
    ref: "el",
    tabindex: "-1",
    class: normalizeClass(["baklava-editor", {
      "baklava-ignore-mouse": !!_ctx.temporaryConnection || _ctx.dragging,
      "--temporary-connection": !!_ctx.temporaryConnection
    }]),
    onPointermove: _cache[0] || (_cache[0] = withModifiers((...args) => _ctx.onPointerMove && _ctx.onPointerMove(...args), ["self"])),
    onPointerdown: _cache[1] || (_cache[1] = (...args) => _ctx.onPointerDown && _ctx.onPointerDown(...args)),
    onPointerup: _cache[2] || (_cache[2] = (...args) => _ctx.onPointerUp && _ctx.onPointerUp(...args)),
    onWheel: _cache[3] || (_cache[3] = withModifiers((...args) => _ctx.mouseWheel && _ctx.mouseWheel(...args), ["self"])),
    onKeydown: _cache[4] || (_cache[4] = (...args) => _ctx.keyDown && _ctx.keyDown(...args)),
    onKeyup: _cache[5] || (_cache[5] = (...args) => _ctx.keyUp && _ctx.keyUp(...args))
  }, [
    renderSlot(_ctx.$slots, "background", {}, () => [
      createVNode(_component_background)
    ]),
    renderSlot(_ctx.$slots, "toolbar", {}, () => [
      createVNode(_component_toolbar)
    ]),
    renderSlot(_ctx.$slots, "palette", {}, () => [
      createVNode(_component_node_palette)
    ]),
    (openBlock(), createElementBlock("svg", _hoisted_1, [
      (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.connections, (connection) => {
        return openBlock(), createElementBlock("g", {
          key: connection.id + _ctx.counter.toString()
        }, [
          renderSlot(_ctx.$slots, "connection", { connection }, () => [
            createVNode(_component_connection_wrapper, { connection }, null, 8, ["connection"])
          ])
        ]);
      }), 128)),
      renderSlot(_ctx.$slots, "temporaryConnection", { temporaryConnection: _ctx.temporaryConnection }, () => [
        _ctx.temporaryConnection ? (openBlock(), createBlock(_component_temporary_connection, {
          key: 0,
          connection: _ctx.temporaryConnection
        }, null, 8, ["connection"])) : createCommentVNode("", true)
      ])
    ])),
    createElementVNode("div", {
      class: "node-container",
      style: normalizeStyle(_ctx.nodeContainerStyle)
    }, [
      createVNode(TransitionGroup, { name: "fade" }, {
        default: withCtx(() => [
          (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.nodes, (node) => {
            return renderSlot(_ctx.$slots, "node", {
              node,
              selected: _ctx.selectedNodes.includes(node),
              onSelect: ($event) => _ctx.selectNode(node)
            }, () => [
              (openBlock(), createBlock(_component_node, {
                key: node.id + _ctx.counter.toString(),
                node,
                selected: _ctx.selectedNodes.includes(node),
                onSelect: ($event) => _ctx.selectNode(node)
              }, null, 8, ["node", "selected", "onSelect"]))
            ]);
          }), 256))
        ]),
        _: 3
      })
    ], 4),
    renderSlot(_ctx.$slots, "sidebar", {}, () => [
      createVNode(_component_sidebar)
    ]),
    renderSlot(_ctx.$slots, "minimap", {}, () => [
      _ctx.viewModel.settings.enableMinimap ? (openBlock(), createBlock(_component_minimap, { key: 0 })) : createCommentVNode("", true)
    ])
  ], 34);
}
const Editor = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render]]);
function useHotkeyHandler(executeCommand) {
  const pressedKeys = ref([]);
  const handlers = ref([]);
  const handleKeyDown = (ev) => {
    if (!pressedKeys.value.includes(ev.key)) {
      pressedKeys.value.push(ev.key);
    }
    handlers.value.forEach((h) => {
      if (h.keys.every((k) => pressedKeys.value.includes(k))) {
        executeCommand(h.commandName);
      }
    });
  };
  const handleKeyUp = (ev) => {
    const index = pressedKeys.value.indexOf(ev.key);
    if (index >= 0) {
      pressedKeys.value.splice(index, 1);
    }
  };
  const registerHotkey = (keys2, commandName) => {
    handlers.value.push({ keys: keys2, commandName });
  };
  return { pressedKeys, handleKeyDown, handleKeyUp, registerHotkey };
}
const useCommandHandler = () => {
  const commands = ref(/* @__PURE__ */ new Map());
  const registerCommand = (name, command) => {
    if (commands.value.has(name)) {
      throw new Error(`Command "${name}" already exists`);
    }
    commands.value.set(name, command);
  };
  const executeCommand = (name, throwOnNonexisting = false, ...args) => {
    if (!commands.value.has(name)) {
      if (throwOnNonexisting) {
        throw new Error(`[CommandHandler] Command ${name} not registered`);
      } else {
        return;
      }
    }
    return commands.value.get(name).execute(...args);
  };
  const canExecuteCommand = (name, throwOnNonexisting = false, ...args) => {
    if (!commands.value.has(name)) {
      if (throwOnNonexisting) {
        throw new Error(`[CommandHandler] Command ${name} not registered`);
      } else {
        return false;
      }
    }
    return commands.value.get(name).canExecute(args);
  };
  const hotkeyHandler = useHotkeyHandler(executeCommand);
  return reactive({ registerCommand, executeCommand, canExecuteCommand, ...hotkeyHandler });
};
const isTemplate = (g) => !(g instanceof Graph);
function useSwitchGraph(editor, displayedGraph) {
  const switchGraph = (newGraph) => {
    let newGraphInstance;
    if (!isTemplate(newGraph)) {
      if (newGraph !== editor.value.graph) {
        throw new Error(
          "Can only switch using 'Graph' instance when it is the root graph. Otherwise a 'GraphTemplate' must be used."
        );
      }
      newGraphInstance = newGraph;
    } else {
      newGraphInstance = new Graph(editor.value);
      newGraph.createGraph(newGraphInstance);
      newGraphInstance.inputs.forEach((input) => {
        const node = new SubgraphInputNode();
        node.inputs.name.value = input.name;
        node.graphInterfaceId = input.id;
        newGraphInstance.addNode(node);
        const targetInterface = newGraphInstance.findNodeInterface(input.nodeInterfaceId);
        if (!targetInterface) {
          console.warn(`Could not find target interface ${input.nodeInterfaceId} for subgraph input node`);
          return;
        }
        newGraphInstance.addConnection(node.outputs.placeholder, targetInterface);
      });
      newGraphInstance.outputs.forEach((output) => {
        const node = new SubgraphOutputNode();
        node.inputs.name.value = output.name;
        node.graphInterfaceId = output.id;
        newGraphInstance.addNode(node);
        const targetInterface = newGraphInstance.findNodeInterface(output.nodeInterfaceId);
        if (!targetInterface) {
          console.warn(`Could not find target interface ${output.nodeInterfaceId} for subgraph input node`);
          return;
        }
        newGraphInstance.addConnection(targetInterface, node.inputs.placeholder);
      });
    }
    if (displayedGraph.value && displayedGraph.value !== editor.value.graph) {
      displayedGraph.value.destroy();
    }
    newGraphInstance.panning = newGraphInstance.panning ?? newGraph.panning ?? { x: 0, y: 0 };
    newGraphInstance.scaling = newGraphInstance.scaling ?? newGraph.scaling ?? 1;
    newGraphInstance.selectedNodes = newGraphInstance.selectedNodes ?? [];
    newGraphInstance.sidebar = newGraphInstance.sidebar ?? { visible: false, nodeId: "", optionName: "" };
    displayedGraph.value = newGraphInstance;
  };
  return { switchGraph };
}
function setViewNodeProperties(node) {
  node.position = node.position ?? { x: 0, y: 0 };
  node.disablePointerEvents = false;
  node.twoColumn = node.twoColumn ?? false;
  node.width = node.width ?? 200;
}
function useBaklava(existingEditor) {
  const editor = ref(existingEditor ?? new Editor$1());
  const token = Symbol("ViewModelToken");
  const _displayedGraph = ref(null);
  const displayedGraph = shallowReadonly(_displayedGraph);
  const { switchGraph } = useSwitchGraph(editor, _displayedGraph);
  const isSubgraph = computed(() => displayedGraph.value && displayedGraph.value !== editor.value.graph);
  const settings = reactive({
    useStraightConnections: false,
    enableMinimap: false,
    background: {
      gridSize: 100,
      gridDivision: 5,
      subGridVisibleThreshold: 0.6
    }
  });
  const commandHandler = useCommandHandler();
  const history = useHistory(displayedGraph, commandHandler);
  const clipboard = useClipboard(displayedGraph, editor, commandHandler);
  const hooks = {
    renderNode: new SequentialHook(null),
    renderInterface: new SequentialHook(null)
  };
  registerGraphCommands(displayedGraph, commandHandler, switchGraph);
  registerSidebarCommands(displayedGraph, commandHandler);
  watch(
    editor,
    (newValue, oldValue) => {
      if (oldValue) {
        oldValue.events.registerGraph.unsubscribe(token);
        oldValue.graphEvents.beforeAddNode.unsubscribe(token);
        newValue.nodeHooks.beforeLoad.unsubscribe(token);
        newValue.nodeHooks.afterSave.unsubscribe(token);
        newValue.graphTemplateHooks.beforeLoad.unsubscribe(token);
        newValue.graphTemplateHooks.afterSave.unsubscribe(token);
      }
      if (newValue) {
        newValue.nodeHooks.beforeLoad.subscribe(token, (state, node) => {
          node.position = state.position ?? { x: 0, y: 0 };
          node.width = state.width ?? 200;
          node.twoColumn = state.twoColumn ?? false;
          return state;
        });
        newValue.nodeHooks.afterSave.subscribe(token, (state, node) => {
          state.position = node.position;
          state.width = node.width;
          state.twoColumn = node.twoColumn;
          return state;
        });
        newValue.graphTemplateHooks.beforeLoad.subscribe(token, (state, template) => {
          template.panning = state.panning;
          template.scaling = state.scaling;
          return state;
        });
        newValue.graphTemplateHooks.afterSave.subscribe(token, (state, template) => {
          state.panning = template.panning;
          state.scaling = template.scaling;
          return state;
        });
        newValue.graphEvents.beforeAddNode.subscribe(token, (node) => setViewNodeProperties(node));
        editor.value.registerNodeType(SubgraphInputNode, { category: "Subgraphs" });
        editor.value.registerNodeType(SubgraphOutputNode, { category: "Subgraphs" });
        switchGraph(newValue.graph);
      }
    },
    { immediate: true }
  );
  return reactive({
    editor,
    displayedGraph,
    isSubgraph,
    settings,
    commandHandler,
    history,
    clipboard,
    hooks,
    switchGraph
  });
}
const components = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Connection: ConnectionView,
  ConnectionWrapper,
  TemporaryConnection,
  Node: _sfc_main$q,
  NodeInterface,
  ContextMenu,
  Minimap,
  Sidebar
}, Symbol.toStringTag, { value: "Module" }));
export {
  ButtonInterface,
  ButtonInterfaceComponent,
  CheckboxInterface,
  CheckboxInterfaceComponent,
  commandList as Commands,
  components as Components,
  Editor as EditorComponent,
  IntegerInterface,
  IntegerInterfaceComponent,
  NumberInterface,
  NumberInterfaceComponent,
  SelectInterface,
  SelectInterfaceComponent,
  SliderInterface,
  SliderInterfaceComponent,
  TextInputInterface,
  TextInputInterfaceComponent,
  TextInterface,
  providePlugin,
  useBaklava,
  useCommandHandler,
  useDragMove,
  useGraph,
  useTransform,
  useViewModel
};
